import type { EnFormatPageProps } from "@/components/site/en/EnFormatPage";

export interface EnFormatEntry {
  enPath: string;
  svPath: string;
  meta: { title: string; description: string };
  page: EnFormatPageProps;
}

export const EN_FORMATS: Record<string, EnFormatEntry> = {
  commercials: {
    enPath: "/en/commercials",
    svPath: "/reklamfilm",
    meta: {
      title: "Commercials — Premium TV & online ads | Fenny",
      description:
        "Commercials that earn attention and drive conversion. Strategy, scripting, shoot and multi-format delivery — fixed price, senior crew.",
    },
    page: {
      eyebrow: "Commercials",
      heading: "Commercials people actually watch.",
      highlightIndices: [2, 3],
      intro:
        "We make TV-quality commercials for online, broadcast and retail screens — the kind that hold attention past second three and move real numbers.",
      whatItIs: {
        title: "Story-led ads built for measurable response.",
        body: "A commercial is your strongest single piece of marketing collateral. Done right, it works as a brand asset for years and as a paid-media unit from day one. We script, shoot and edit with both jobs in mind.",
      },
      whenToUse: [
        "Launching a new product, service or category",
        "Re-positioning the brand for a wider audience",
        "Going hard on paid media for a quarter or campaign window",
        "Replacing tired creative on Meta, YouTube or linear TV",
      ],
      whatsIncluded: [
        "Concept and script development",
        "Senior director, DP and on-set producer",
        "Casting and location scouting",
        "1-day or 2-day shoot",
        "Edit, color grade and sound design",
        "Original music or licensed track",
        "Hero film (30s / 60s) plus cutdowns (15s, 9:16, 1:1)",
        "Captions and platform-ready masters",
      ],
      outcomes: [
        { metric: "2-3×", body: "lift in branded recall vs. static creative." },
        { metric: "30-50%", body: "lower CPM on paid social with right-sized cutdowns." },
        { metric: "12-24 mo", body: "useful life — campaigns rarely burn out in one cycle." },
      ],
      faq: [
        {
          q: "How long does a commercial take from brief to delivery?",
          a: "Typically four to six weeks: one for concept, two for pre-production, one shoot day and two weeks for post.",
        },
        {
          q: "Can we shoot multiple variations the same day?",
          a: "Yes. Most clients leave a single shoot day with a hero film plus 4-8 cutdowns and stills for static placements.",
        },
      ],
    },
  },
  "corporate-video": {
    enPath: "/en/corporate-video",
    svPath: "/foretagsfilm",
    meta: {
      title: "Corporate video — Clear, modern company films | Fenny",
      description:
        "Corporate video that explains what you do, who you are and why it matters — without the jargon. Fixed price, senior crew, fast turnaround.",
    },
    page: {
      eyebrow: "Corporate video",
      heading: "Company films without the boardroom feel.",
      highlightIndices: [3],
      intro:
        "Corporate video that explains what you do, why it matters and who's behind it — in a way real humans actually want to watch.",
      whatItIs: {
        title: "The film your website, sales deck and onboarding all share.",
        body: "A modern corporate film is a single, well-shot piece of work that lives on the home page, in the sales deck, in onboarding and as the cornerstone of recruitment campaigns. We build it to do all four jobs at once.",
      },
      whenToUse: [
        "Refreshing a stale website hero",
        "Pitching investors or boards",
        "Sales enablement for enterprise deals",
        "Internal alignment after a re-brand or merger",
      ],
      whatsIncluded: [
        "Discovery workshop and key-message map",
        "Script and interview guide",
        "1-day shoot at your offices or on location",
        "Edit, color, sound and motion graphics",
        "Hero film (90-120s) plus 15s and 30s teaser cutdowns",
        "Subtitled masters for web and social",
      ],
      outcomes: [
        { metric: "+38%", body: "average time-on-page when added above the fold." },
        { metric: "1 day", body: "of filming usually covers everything we need." },
        { metric: "3-5 yr", body: "shelf life when the story is evergreen." },
      ],
    },
  },
  "brand-film": {
    enPath: "/en/brand-film",
    svPath: "/varumarkesfilm",
    meta: {
      title: "Brand film — Anthems that build long-term equity | Fenny",
      description:
        "A brand film is your story told in motion. Cinematic craft, strategic narrative, and assets that compound in value over time.",
    },
    page: {
      eyebrow: "Brand film",
      heading: "The anthem your brand keeps coming back to.",
      highlightIndices: [1],
      intro:
        "A brand film is the one piece of content you'll still be proud of in five years. Cinematic, considered and built to compound in value with every view.",
      whatItIs: {
        title: "Your story, in motion — built for permanence.",
        body: "Where a commercial sells this quarter, a brand film defines who you are for the next half decade. We invest in story, craft and music that earn the longer shelf life.",
      },
      whenToUse: [
        "Launching or refreshing the brand identity",
        "Anchoring a multi-year category move",
        "Recruitment at scale across multiple markets",
        "IPO, funding round or board-level storytelling",
      ],
      whatsIncluded: [
        "Brand-narrative workshop with senior strategist",
        "Two-pass script with creative direction",
        "Senior director and DP, 1-2 shoot days",
        "Casting, location, art direction",
        "Color grade in a graded suite",
        "Original score or premium licensed music",
        "Master plus social cutdowns and stills package",
      ],
      outcomes: [
        { metric: "5+ yr", body: "useful life when the story is brand-led." },
        { metric: "8×", body: "social engagement vs. product-led ads (Meta benchmark)." },
        { metric: "1", body: "asset that powers website, recruitment, sales and PR." },
      ],
    },
  },
  "product-video": {
    enPath: "/en/product-video",
    svPath: "/produktfilm",
    meta: {
      title: "Product video — Show what it does, why it matters | Fenny",
      description:
        "Product video for e-commerce, B2B and SaaS. Hero films, demos, lifestyle cutdowns and 360° turntables in one shoot.",
    },
    page: {
      eyebrow: "Product video",
      heading: "Show what it does. Sell what it means.",
      highlightIndices: [2, 5],
      intro:
        "Product video that does both jobs in one shoot — the functional demo your conversion pages need, and the lifestyle hero your paid social needs.",
      whatItIs: {
        title: "From PDP to paid social, one production cycle.",
        body: "We plan the shoot around every angle and use case in advance so a single day on set produces hero films, demo cuts, lifestyle stills and 360° turntables.",
      },
      whenToUse: [
        "New product launches",
        "Refreshing top-selling SKUs that lack motion",
        "Adding social and PDP video before peak season",
        "B2B demos and how-it-works explainers",
      ],
      whatsIncluded: [
        "Shot list mapped to your channels",
        "Studio or location shoot, 1 day",
        "Macro and detail rigs for tactile shots",
        "Lifestyle talent if relevant",
        "Edit, color, sound, motion graphics",
        "Hero, demo and short-form cutdowns",
        "360° turntable export when needed",
      ],
      outcomes: [
        { metric: "+34%", body: "PDP conversion lift with video above the fold (Shopify)." },
        { metric: "1 day", body: "shoot covers hero + 6-8 cutdowns + stills." },
        { metric: "2-3 wk", body: "from brief to first cut." },
      ],
    },
  },
  "recruitment-video": {
    enPath: "/en/recruitment-video",
    svPath: "/rekryteringsfilm",
    meta: {
      title: "Recruitment video that pre-qualifies candidates | Fenny",
      description:
        "Recruitment video that attracts the candidates you actually want. Employer brand films, role-specific shorts and recruiter-led cuts.",
    },
    page: {
      eyebrow: "Recruitment video",
      heading: "Hire faster with film that pre-qualifies.",
      highlightIndices: [4],
      intro:
        "Recruitment video that attracts the right candidates and filters out the wrong ones — before HR has to.",
      whatItIs: {
        title: "Employer-brand storytelling that hiring managers will thank you for.",
        body: "A great recruitment film does two jobs: it makes the role feel real and it gives candidates a self-test for fit. We build both into the script.",
      },
      whenToUse: [
        "Scaling a team faster than referrals can keep up",
        "Opening a new office or market",
        "Filling hard-to-hire technical or specialist roles",
        "Lowering cost per qualified application",
      ],
      whatsIncluded: [
        "Interviews with current employees",
        "On-site filming across 1-2 days",
        "Hero employer-brand film (60-90s)",
        "Role-specific shorts (15-30s) per opening",
        "Vertical cutdowns for LinkedIn, TikTok and Instagram",
        "Captions and recruiter-share-ready edits",
      ],
      outcomes: [
        { metric: "-40%", body: "average cost per qualified applicant." },
        { metric: "3×", body: "candidate page time vs. text-only roles." },
        { metric: "6-12 mo", body: "useful life per recruitment film." },
      ],
    },
  },
  "training-video": {
    enPath: "/en/training-video",
    svPath: "/utbildningsfilm",
    meta: {
      title: "Training video — Make learning stick | Fenny",
      description:
        "Training and instructional video that replaces the SOP no one reads. Modular, multilingual, made to onboard at scale.",
    },
    page: {
      eyebrow: "Training video",
      heading: "Onboard at scale without losing the human touch.",
      highlightIndices: [4],
      intro:
        "Training video that replaces the 80-page SOP no one reads — modular, multilingual and made to ship at scale.",
      whatItIs: {
        title: "The new SOP. Watchable in 90 seconds.",
        body: "We split your processes into short, modular films your team can revisit on demand. Easier to update, easier to translate, easier to enforce.",
      },
      whenToUse: [
        "Onboarding new hires across multiple offices",
        "Rolling out a new tool, process or SOP",
        "Compliance and safety training",
        "Customer training that reduces support tickets",
      ],
      whatsIncluded: [
        "Curriculum and module breakdown",
        "Script and storyboards per module",
        "Studio or on-site shoot",
        "Motion graphics for diagrams and UI",
        "Captions and translations as needed",
        "LMS-ready exports (SCORM, MP4, HLS)",
      ],
      outcomes: [
        { metric: "-25%", body: "ramp time for new hires on average." },
        { metric: "+47%", body: "knowledge retention vs. text-only SOPs." },
        { metric: "x langs", body: "the same shoot covers multiple language versions." },
      ],
    },
  },
  "event-film": {
    enPath: "/en/event-film",
    svPath: "/eventfilm",
    meta: {
      title: "Event film — keep the energy after the event | Fenny",
      description:
        "Event film and aftermovies that turn a single day into 12 months of marketing. Same-day social cuts and a polished hero film.",
    },
    page: {
      eyebrow: "Event film",
      heading: "Turn one event into a year of marketing.",
      highlightIndices: [2, 6],
      intro:
        "Event film that captures the energy as it happens — and turns it into assets you'll re-use for the next twelve months.",
      whatItIs: {
        title: "Same-day socials plus a hero aftermovie.",
        body: "A senior crew on the floor, a producer running shot priorities, an editor working off-site in parallel. You leave with same-day social cuts and a hero aftermovie within the week.",
      },
      whenToUse: [
        "Conferences and customer summits",
        "Product launch events",
        "Sponsor activations",
        "Annual kick-offs and offsites",
      ],
      whatsIncluded: [
        "Pre-event run-of-show planning",
        "2-4 person crew on event days",
        "Same-day social cuts (15-30s)",
        "Speaker reels and panel highlights",
        "Hero aftermovie (60-120s)",
        "B-roll archive for future campaigns",
      ],
      outcomes: [
        { metric: "Day-0", body: "first cuts live before guests get home." },
        { metric: "5-7 d", body: "hero aftermovie delivery." },
        { metric: "12 mo", body: "of usable footage for next year's promo." },
      ],
    },
  },
  documentary: {
    enPath: "/en/documentary",
    svPath: "/dokumentarfilm",
    meta: {
      title: "Documentary — Long-form story, real stakes | Fenny",
      description:
        "Documentary production for brands, foundations and broadcasters. Story-led, research-driven, cinematic craft.",
    },
    page: {
      eyebrow: "Documentary",
      heading: "True stories, told with cinematic care.",
      highlightIndices: [0],
      intro:
        "Documentary production for brands, foundations and broadcasters — story-led, research-driven, and made to hold attention across the full runtime.",
      whatItIs: {
        title: "Long-form storytelling with real characters and real stakes.",
        body: "Documentaries demand more research, more time on the ground and more editorial discipline. We bring all three — plus the cinematic craft that makes a 20-minute film feel like five.",
      },
      whenToUse: [
        "Brand documentaries (origin, founder, mission)",
        "Cause-led films for foundations and NGOs",
        "Long-form customer-story films",
        "Editorial pieces for broadcasters and streamers",
      ],
      whatsIncluded: [
        "Research and story-development phase",
        "Director-led interviews",
        "Multi-day filming across locations",
        "Archive sourcing and rights clearance",
        "Color, sound mix and original score",
        "Festival-ready master and broadcast variants",
      ],
      outcomes: [
        { metric: "10-30 min", body: "typical runtime per documentary." },
        { metric: "8-16 wk", body: "from research to delivery." },
        { metric: "Awards", body: "festival-grade craft built in by default." },
      ],
    },
  },
  "image-film": {
    enPath: "/en/image-film",
    svPath: "/imagefilm",
    meta: {
      title: "Image film — The film for your home page | Fenny",
      description:
        "Image films that define how the world sees your company. Strategic, cinematic and built for the long term.",
    },
    page: {
      eyebrow: "Image film",
      heading: "The film that introduces you to the world.",
      highlightIndices: [4, 6],
      intro:
        "An image film is the single piece of motion that defines how the market, your candidates and your customers first perceive you. We make it count.",
      whatItIs: {
        title: "A flagship asset built to last.",
        body: "Image films sit on the home page, lead investor decks and open every important meeting. They are the one film you want to still be proud of in three years.",
      },
      whenToUse: [
        "New brand or rebrand launches",
        "Capital raises and IPOs",
        "Strategic market entry",
        "Annual report storytelling",
      ],
      whatsIncluded: [
        "Strategy and creative direction",
        "Senior director, DP, art direction",
        "1-2 shoot days, hero locations",
        "Color grade, sound design, original music",
        "Hero (60-120s) plus 15s and 30s cuts",
        "Stills package from the shoot",
      ],
      outcomes: [
        { metric: "Home page", body: "default placement above the fold." },
        { metric: "3-5 yr", body: "useful life when story is brand-led." },
        { metric: "1", body: "asset, dozens of internal and external uses." },
      ],
    },
  },
  "campaign-film": {
    enPath: "/en/campaign-film",
    svPath: "/kampanjfilm",
    meta: {
      title: "Campaign film for every channel, one shoot | Fenny",
      description:
        "Campaign films that ship as a full asset set: hero, social cutdowns, paid variants and OOH stills — from a single shoot.",
    },
    page: {
      eyebrow: "Campaign film",
      heading: "One shoot. A whole campaign.",
      highlightIndices: [2, 4],
      intro:
        "Campaign films built around a single creative idea — but shipped as the full asset set every channel actually needs.",
      whatItIs: {
        title: "Hero + cutdowns + statics, mapped to the media plan.",
        body: "We design the shoot from the media plan backwards: which formats need to ship, where, when. The result is one production day producing the entire campaign asset library.",
      },
      whenToUse: [
        "Seasonal campaigns and product moments",
        "Multi-channel launches",
        "Always-on paid social refreshes",
        "OOH plus digital pairings",
      ],
      whatsIncluded: [
        "Creative concept and channel plan",
        "Single shoot covering all variants",
        "Hero film, 6-12 cutdowns",
        "9:16, 1:1, 16:9 ready masters",
        "OOH-grade stills",
        "Captions and platform-spec exports",
      ],
      outcomes: [
        { metric: "1 shoot", body: "delivers the full asset set for the campaign." },
        { metric: "-30%", body: "campaign production cost vs. piecemeal." },
        { metric: "4-6 wk", body: "from brief to full delivery." },
      ],
    },
  },
  "instructional-video": {
    enPath: "/en/instructional-video",
    svPath: "/instruktionsfilm",
    meta: {
      title: "Instructional video that replaces the manual | Fenny",
      description:
        "Instructional video for setup, install, how-to-use and troubleshooting. Modular, multilingual, support-ticket-reducing.",
    },
    page: {
      eyebrow: "Instructional video",
      heading: "Show, don't tell. Then ship support tickets to zero.",
      highlightIndices: [1],
      intro:
        "Instructional video that replaces the install manual customers won't read — and the support ticket queue they'd otherwise generate.",
      whatItIs: {
        title: "Bite-sized how-to films that scale.",
        body: "We break down setup, install, use and troubleshooting into short modules customers can follow on the device they're holding. Same-shoot, multiple languages.",
      },
      whenToUse: [
        "Hardware products requiring setup",
        "Apps and platforms with onboarding friction",
        "Self-service customer support",
        "Field service technician training",
      ],
      whatsIncluded: [
        "Process mapping with your support team",
        "Storyboard per module",
        "Studio or on-site filming",
        "Motion graphics for UI and diagrams",
        "Multilingual captions or voiceover",
        "Embedded-player-ready exports",
      ],
      outcomes: [
        { metric: "-30%", body: "support ticket volume on covered topics." },
        { metric: "90s", body: "average module length." },
        { metric: "Modular", body: "update one module without re-shooting the rest." },
      ],
    },
  },
  "internal-communication-video": {
    enPath: "/en/internal-communication-video",
    svPath: "/internkommunikation-film",
    meta: {
      title: "Internal communication video for teams | Fenny",
      description:
        "Internal video for CEO updates, town halls, strategy rollouts and culture content. Calm, professional, fast turnaround.",
    },
    page: {
      eyebrow: "Internal communication",
      heading: "Talk to the whole company without sending another email.",
      highlightIndices: [4],
      intro:
        "Internal communication video that lands the message — for strategy rollouts, leadership updates and culture moments that emails simply can't carry.",
      whatItIs: {
        title: "Calm, professional internal video on a predictable cadence.",
        body: "We help comms and HR teams run a regular drumbeat of internal video — CEO updates, all-hands recaps, strategy explainers — without burning out internal resources.",
      },
      whenToUse: [
        "Quarterly CEO updates",
        "Strategy rollouts and OKR launches",
        "Acquisition or restructure announcements",
        "Culture, values and recognition content",
      ],
      whatsIncluded: [
        "Message workshop with leadership",
        "Light-touch studio or office setup",
        "Edit, motion graphics, captions",
        "Internal-network-ready exports",
        "Recurring cadence via a Pulse plan if needed",
      ],
      outcomes: [
        { metric: "5×", body: "completion rate vs. all-hands emails." },
        { metric: "Same week", body: "shoot to upload on standard updates." },
        { metric: "Quiet", body: "calm sets — no disruption to teams." },
      ],
    },
  },
  "customer-story-video": {
    enPath: "/en/customer-story-video",
    svPath: "/kundcase-film",
    meta: {
      title: "Customer story video that sells for you | Fenny",
      description:
        "Customer story films for B2B sales enablement. Real customers, real metrics, on-message and on-brand.",
    },
    page: {
      eyebrow: "Customer story video",
      heading: "Your best salesperson is already a customer.",
      highlightIndices: [4],
      intro:
        "Customer story films that move enterprise deals through the funnel — with real customers, real metrics and a senior crew that lets them be themselves on camera.",
      whatItIs: {
        title: "Sales enablement, on film.",
        body: "We script around the questions your prospects actually ask, capture customers on their own turf and edit for the moment in the sales cycle where the film does the most work.",
      },
      whenToUse: [
        "Enterprise sales enablement",
        "ABM campaigns targeting named accounts",
        "Industry-specific landing pages",
        "Analyst and partner briefings",
      ],
      whatsIncluded: [
        "Customer prep and interview guide",
        "On-site filming at customer location",
        "Hero film (90-180s) plus 30s and 15s",
        "Vertical cuts for LinkedIn and ABM",
        "Pull-quote stills for sales decks",
      ],
      outcomes: [
        { metric: "+27%", body: "win rate when customer film is shared mid-cycle." },
        { metric: "1 visit", body: "per customer covers all asset variants." },
        { metric: "Library", body: "build a sector-by-sector reference set." },
      ],
    },
  },
  "testimonial-video": {
    enPath: "/en/testimonial-video",
    svPath: "/testimonial-film",
    meta: {
      title: "Testimonial video — social proof on demand | Fenny",
      description:
        "Testimonial video filmed in batches. 30-90 seconds, multiple customers per shoot day, ready for ads, landing pages and sales decks.",
    },
    page: {
      eyebrow: "Testimonial video",
      heading: "Social proof, on tap.",
      highlightIndices: [2],
      intro:
        "Short, sharp testimonial films you can drop into ads, landing pages and sales decks the day they're needed.",
      whatItIs: {
        title: "Batch-shot testimonials for always-on use.",
        body: "We schedule 3-6 customers in a single shoot day, run a tight interview format and edit into modular 30-90s units. Built for paid, organic and sales contexts in parallel.",
      },
      whenToUse: [
        "Paid social and retargeting",
        "Landing-page conversion lifts",
        "Sales deck and proposal inserts",
        "Trust-building during a launch quarter",
      ],
      whatsIncluded: [
        "Customer outreach support",
        "Tight interview structure",
        "1 shoot day = 3-6 testimonials",
        "30s, 60s and 9:16 vertical cuts",
        "Captions baked in for sound-off viewing",
      ],
      outcomes: [
        { metric: "+19%", body: "average landing-page conversion lift." },
        { metric: "6×", body: "cost per testimonial vs. one-by-one shoots." },
        { metric: "1 wk", body: "from shoot to ad-ready cuts." },
      ],
    },
  },
  "3d-animation": {
    enPath: "/en/3d-animation",
    svPath: "/3d-animation",
    meta: {
      title: "3D animation — Show what can't be filmed | Fenny",
      description:
        "3D animation and CGI for product reveals, technical explainers and brand worlds you can't shoot in real life.",
    },
    page: {
      eyebrow: "3D animation",
      heading: "Show what a camera never could.",
      highlightIndices: [3],
      intro:
        "3D animation and CGI for products, machinery and concepts that live beyond the reach of a real-world camera — rendered with cinematic craft.",
      whatItIs: {
        title: "Photoreal CGI built for marketing and explanation.",
        body: "From product reveals and technical cutaways to fully imagined brand worlds, we model, light and render in 3D so the result feels as considered as a live-action commercial.",
      },
      whenToUse: [
        "Products that don't physically exist yet",
        "Internal mechanics impossible to film",
        "Hero campaigns needing impossible camera moves",
        "Architecture, automotive and industrial reveals",
      ],
      whatsIncluded: [
        "Concept and storyboard",
        "3D modelling and texturing",
        "Lighting, animation and camera direction",
        "Compositing, color grade and sound design",
        "Hero film plus social cutdowns",
      ],
      outcomes: [
        { metric: "Photoreal", body: "indistinguishable from live action when needed." },
        { metric: "Re-usable", body: "assets re-rendered into next year's campaign." },
        { metric: "4-8 wk", body: "typical timeline from brief to delivery." },
      ],
    },
  },
  "motion-graphics": {
    enPath: "/en/motion-graphics",
    svPath: "/motion-graphics",
    meta: {
      title: "Motion graphics — Explain anything in 60 seconds | Fenny",
      description:
        "Motion graphics for explainers, data, UI and brand systems. Crisp design, sharp timing, native brand feel.",
    },
    page: {
      eyebrow: "Motion graphics",
      heading: "Make the abstract feel obvious.",
      highlightIndices: [2],
      intro:
        "Motion graphics that explain complex ideas, animate data and bring brand systems to life — designed for clarity, not decoration.",
      whatItIs: {
        title: "Design that moves with purpose.",
        body: "Whether it's a 60-second explainer, animated UI for a product demo or graphic layers inside a live-action film, motion graphics translate ideas into something the eye understands instantly.",
      },
      whenToUse: [
        "Explainer videos for new products or services",
        "Data and report visualisation",
        "Animated UI for app and SaaS demos",
        "Brand bumpers, lower-thirds and graphic systems",
      ],
      whatsIncluded: [
        "Script and storyboard",
        "Design system and style frames",
        "Animation, sound design, voiceover",
        "Captions and platform-ready exports",
      ],
      outcomes: [
        { metric: "60-90s", body: "average explainer length, optimised for completion." },
        { metric: "+74%", body: "comprehension lift vs. static infographics." },
        { metric: "Re-usable", body: "design system extends to future films." },
      ],
    },
  },
  "b-roll": {
    enPath: "/en/b-roll",
    svPath: "/b-roll-inspelning",
    meta: {
      title: "B-roll — Cinematic supporting footage on demand | Fenny",
      description:
        "B-roll shoots for brand libraries, editorial use and ongoing content. A senior crew, a day on site, months of usable footage.",
    },
    page: {
      eyebrow: "B-roll",
      heading: "Build the footage library your team keeps reaching for.",
      highlightIndices: [3],
      intro:
        "B-roll shoots that give marketing, comms and PR a deep, cinematic library to draw from for the next twelve months — instead of paying for stock.",
      whatItIs: {
        title: "A planned day on site, a year of usable footage.",
        body: "We map every space, process and team you want covered, then run a focused shoot day. The result is a tagged library of cinematic clips ready to drop into any future film.",
      },
      whenToUse: [
        "Building a brand footage library",
        "Supporting recurring social and PR needs",
        "Ahead of a busy campaign quarter",
        "Refreshing tired or off-brand stock footage",
      ],
      whatsIncluded: [
        "Pre-shoot brief and shot list",
        "1-2 day shoot with senior DP",
        "Color graded master clips",
        "Tagged and organised delivery folder",
        "Usage rights for all internal and external channels",
      ],
      outcomes: [
        { metric: "100+", body: "tagged clips from a single shoot day." },
        { metric: "12 mo", body: "of supporting footage for future films." },
        { metric: "-60%", body: "spend vs. licensing equivalent premium stock." },
      ],
    },
  },
  "live-streaming": {
    enPath: "/en/live-streaming",
    svPath: "/livesandning",
    meta: {
      title: "Live streaming — broadcast-grade events | Fenny",
      description:
        "Live streaming for conferences, launches and town halls. Multi-camera, branded graphics, encoded for any platform.",
    },
    page: {
      eyebrow: "Live streaming",
      heading: "Broadcast-grade live, without the broadcast headache.",
      highlightIndices: [2],
      intro:
        "Live streaming for events, launches and town halls — produced with multi-camera direction, branded graphics and platform-ready encoding.",
      whatItIs: {
        title: "A live broadcast that feels like a finished film.",
        body: "We bring a full multi-camera setup, vision mixing, on-screen graphics and platform-grade encoding so your livestream looks the way your brand deserves.",
      },
      whenToUse: [
        "Conferences and customer summits",
        "Product launches and keynotes",
        "Internal all-hands and town halls",
        "Hybrid events with remote audiences",
      ],
      whatsIncluded: [
        "Pre-event run-of-show",
        "Multi-camera crew and vision mixing",
        "Branded graphics and lower-thirds",
        "Stream encoding to any platform",
        "Recorded master plus speaker cutdowns",
      ],
      outcomes: [
        { metric: "Multi-cam", body: "broadcast-grade direction throughout." },
        { metric: "Any platform", body: "YouTube, LinkedIn, Vimeo, custom RTMP." },
        { metric: "Day-0", body: "speaker reels delivered post-event." },
      ],
    },
  },
  aftermovie: {
    enPath: "/en/aftermovie",
    svPath: "/aftermovie",
    meta: {
      title: "Aftermovie — Relive the event, sell the next one | Fenny",
      description:
        "Aftermovies that capture event energy and drive ticket sales for the next edition. Same-week delivery, cinematic craft.",
    },
    page: {
      eyebrow: "Aftermovie",
      heading: "Sell next year's tickets with this year's energy.",
      highlightIndices: [1],
      intro:
        "Aftermovies designed to do one job: make next year's audience feel like they missed out — and book before tickets sell out.",
      whatItIs: {
        title: "The trailer for the edition that just happened.",
        body: "We approach aftermovies like film trailers: a tight runtime, a hero moment every few seconds and a soundtrack that earns the goosebumps.",
      },
      whenToUse: [
        "Festivals and conferences with annual editions",
        "Brand activations and experiential marketing",
        "Sponsor recap reels",
        "Investor and partner storytelling",
      ],
      whatsIncluded: [
        "On-site crew of 2-4",
        "Senior editor working in parallel",
        "Hero aftermovie (60-120s)",
        "Sponsor and speaker cutdowns",
        "Vertical social edits",
      ],
      outcomes: [
        { metric: "5-7 d", body: "from event close to delivery." },
        { metric: "+22%", body: "early-bird sales lift the following year." },
        { metric: "Festival", body: "grade music and color throughout." },
      ],
    },
  },
  "podcast-video": {
    enPath: "/en/podcast-video",
    svPath: "/podcast-video",
    meta: {
      title: "Podcast video — audio show to content engine | Fenny",
      description:
        "Multi-camera podcast video production. One recording day becomes a full episode plus dozens of social clips.",
    },
    page: {
      eyebrow: "Podcast video",
      heading: "One recording day, a month of content.",
      highlightIndices: [2],
      intro:
        "Podcast video production that turns a single recording day into a polished episode plus dozens of vertical clips ready for social.",
      whatItIs: {
        title: "Multi-camera podcast capture, built for clipping.",
        body: "We record podcasts with a multi-camera setup, broadcast-grade audio and an edit pipeline designed to produce both the long-form episode and high-volume short-form clips.",
      },
      whenToUse: [
        "Founder, expert and customer interview shows",
        "Always-on thought leadership content",
        "Audio podcasts ready to add video",
        "Recurring social-first content engines",
      ],
      whatsIncluded: [
        "Studio or location multi-cam setup",
        "Broadcast-grade audio capture",
        "Full-episode edit with chapters",
        "10-20 vertical social clips per episode",
        "Captioned masters for every platform",
      ],
      outcomes: [
        { metric: "10-20", body: "social clips per recording day." },
        { metric: "+3×", body: "average reach vs. audio-only podcasts." },
        { metric: "Recurring", body: "monthly cadence via Pulse plans." },
      ],
    },
  },
  "video-for-instagram": {
    enPath: "/en/video-for-instagram",
    svPath: "/film-for-instagram",
    meta: {
      title: "Video for Instagram — Reels, stories, ads | Fenny",
      description:
        "Instagram video built for Reels, stories and the feed. Vertical-first, hook-driven, optimised for the algorithm.",
    },
    page: {
      eyebrow: "Video for Instagram",
      heading: "Built for Reels, scored for the algorithm.",
      highlightIndices: [1],
      intro:
        "Instagram video that's planned vertical-first, written hook-first and edited for the way the platform actually rewards attention.",
      whatItIs: {
        title: "Reels, stories and feed video produced as a system.",
        body: "We design the shoot around 9:16 hero cuts, story sequences and feed-native variations — not by squeezing landscape video into a vertical frame after the fact.",
      },
      whenToUse: [
        "Brand growth on Instagram",
        "Reels-first paid campaigns",
        "Always-on social content",
        "Creator-style brand content",
      ],
      whatsIncluded: [
        "Hook-first script and storyboard",
        "Vertical-native shoot direction",
        "Hero Reel plus story and feed cuts",
        "Captions and platform-ready exports",
      ],
      outcomes: [
        { metric: "9:16 first", body: "shot list designed for vertical from day one." },
        { metric: "+45%", body: "completion vs. recut landscape video." },
        { metric: "Reels", body: "ready to publish day of delivery." },
      ],
    },
  },
  "video-for-linkedin": {
    enPath: "/en/video-for-linkedin",
    svPath: "/film-for-linkedin",
    meta: {
      title: "Video for LinkedIn — B2B video that earns attention | Fenny",
      description:
        "LinkedIn video for B2B brands, leaders and recruitment. Short, sharp, captioned, built for sound-off scrolling.",
    },
    page: {
      eyebrow: "Video for LinkedIn",
      heading: "B2B video the feed actually rewards.",
      highlightIndices: [2],
      intro:
        "LinkedIn video for B2B brands, leaders and recruiters — short, sharp, captioned and built for the way the feed scrolls in 2026.",
      whatItIs: {
        title: "Thought leadership and recruitment video, productised.",
        body: "We help founders, executives and brand teams ship LinkedIn video on a steady cadence — leader monologues, recruitment stories, customer proof and category POVs.",
      },
      whenToUse: [
        "Founder and CEO thought leadership",
        "B2B brand growth and ABM",
        "Recruitment and employer brand",
        "Always-on social proof",
      ],
      whatsIncluded: [
        "Message workshop and content plan",
        "Studio or office shoot day",
        "10-20 short LinkedIn clips per day",
        "Hard-coded captions and brand graphics",
      ],
      outcomes: [
        { metric: "Sound-off", body: "edited and captioned for silent scroll." },
        { metric: "Cadence", body: "weekly or bi-weekly via Pulse plans." },
        { metric: "+2.5×", body: "median view-through vs. text-only posts." },
      ],
    },
  },
  "video-for-tiktok": {
    enPath: "/en/video-for-tiktok",
    svPath: "/film-for-tiktok",
    meta: {
      title: "Video for TikTok — native, fast, made to scroll | Fenny",
      description:
        "TikTok video that doesn't look like an ad. Trend-aware, creator-style production with brand-safe craft underneath.",
    },
    page: {
      eyebrow: "Video for TikTok",
      heading: "Made for the For You page, not the brand book.",
      highlightIndices: [3],
      intro:
        "TikTok video that earns its place in the feed — trend-aware, creator-feeling, but built with brand-safe craft underneath.",
      whatItIs: {
        title: "Creator-style production with senior craft.",
        body: "We produce TikTok video that looks native to the platform without sacrificing the things that protect your brand — sound design, color, story discipline and on-message delivery.",
      },
      whenToUse: [
        "Brand awareness in younger audiences",
        "Product launches with social-first budgets",
        "Recruitment for early-career hires",
        "Cultural moments and trend activations",
      ],
      whatsIncluded: [
        "Trend research and hook concepts",
        "Vertical-native shoot day",
        "15-30s native edits in volume",
        "Captions and platform-spec exports",
      ],
      outcomes: [
        { metric: "Native feel", body: "without losing brand safety." },
        { metric: "Batched", body: "10-20 TikToks per shoot day." },
        { metric: "Trend-aware", body: "we adapt to platform shifts." },
      ],
    },
  },
  "video-for-youtube": {
    enPath: "/en/video-for-youtube",
    svPath: "/film-for-youtube",
    meta: {
      title: "Video for YouTube — Long-form, ads and Shorts | Fenny",
      description:
        "YouTube production for brand channels, paid pre-roll and Shorts. Built for watch time, retention and conversion.",
    },
    page: {
      eyebrow: "Video for YouTube",
      heading: "Built for the watch-time graph, not just the thumbnail.",
      highlightIndices: [1],
      intro:
        "YouTube video for brand channels, paid pre-roll and Shorts — produced with the platform's retention mechanics in mind from the script up.",
      whatItIs: {
        title: "Long-form, ad units and Shorts as one system.",
        body: "We design YouTube production around the way watch time actually works: hooks in the first eight seconds, paced reveals through the middle, and Shorts cutdowns that feed the main channel.",
      },
      whenToUse: [
        "Brand-owned YouTube channels",
        "Paid pre-roll and bumper campaigns",
        "Educational and explainer series",
        "Shorts-first audience building",
      ],
      whatsIncluded: [
        "Channel and series planning",
        "Long-form edit with chapters",
        "Pre-roll cutdowns (6s, 15s, 30s)",
        "Shorts variants from the same shoot",
        "Thumbnail design support",
      ],
      outcomes: [
        { metric: "Retention", body: "scripted around the YouTube watch graph." },
        { metric: "1 shoot", body: "feeds long-form, ads and Shorts." },
        { metric: "Recurring", body: "series cadence via Pulse plans." },
      ],
    },
  },
  "social-media-video": {
    enPath: "/en/social-media-video",
    svPath: "/sociala-medier-film",
    meta: {
      title: "Social media video — always-on content | Fenny",
      description:
        "Social media video produced at cadence. One shoot day, every platform, edited and captioned for sound-off scrolling.",
    },
    page: {
      eyebrow: "Social media video",
      heading: "Every channel, one production cycle.",
      highlightIndices: [2],
      intro:
        "Social media video produced as a system: one planned shoot day, edits and cutdowns for every channel, captions baked in for the silent scroll.",
      whatItIs: {
        title: "Always-on social, without always-on chaos.",
        body: "We plan social shoots around the full channel map — Instagram, LinkedIn, TikTok, YouTube Shorts — and ship the assets each platform actually wants, in the formats it actually rewards.",
      },
      whenToUse: [
        "Always-on brand content",
        "Multi-channel paid social",
        "Launch campaigns across platforms",
        "Recurring monthly content drops",
      ],
      whatsIncluded: [
        "Channel map and content plan",
        "Batched shoot day per month",
        "9:16, 1:1 and 16:9 ready masters",
        "Captioned, platform-spec exports",
        "Optional monthly Pulse cadence",
      ],
      outcomes: [
        { metric: "1 day", body: "shoot covers a month of social." },
        { metric: "All channels", body: "edited per-platform, not recycled." },
        { metric: "Cadence", body: "monthly drops via Pulse plans." },
      ],
    },
  },
  // ============= Loop 5 — Service overviews (A4) =============
  "film-production": {
    enPath: "/en/film-production",
    svPath: "/filmproduktion",
    meta: {
      title: "Film production — Full-service production in Sweden | Fenny",
      description:
        "Film production from concept to delivery. Senior crew, fixed price, fast turnaround — for brands, agencies and in-house teams.",
    },
    page: {
      eyebrow: "Film production",
      heading: "Full-service film production, end to end.",
      highlightIndices: [3],
      intro:
        "Film production for brands that want a single accountable partner from concept to delivery — script, shoot, edit, music and motion graphics under one roof.",
      whatItIs: {
        title: "One team, every stage of the production.",
        body: "Full-service production means strategy, pre-production, shoot direction, post and delivery sit with the same senior team. Fewer hand-offs, no agency-in-the-middle markup, a single point of accountability.",
      },
      whenToUse: [
        "Hero campaigns and brand films",
        "Multi-asset launches across channels",
        "When you need senior craft without an in-house team",
        "Replacing a fragmented vendor stack",
      ],
      whatsIncluded: [
        "Strategy and creative direction",
        "Script, storyboard and casting",
        "Senior director, DP and producer on set",
        "Edit, color, sound and motion",
        "Original or licensed music",
        "Channel-ready masters and cutdowns",
      ],
      outcomes: [
        { metric: "Single owner", body: "one producer accountable from brief to final delivery." },
        { metric: "Senior craft", body: "the same crew on every shoot." },
        { metric: "Fixed price", body: "no surprise invoices, ever." },
      ],
    },
  },
  "video-production": {
    enPath: "/en/video-production",
    svPath: "/videoproduktion",
    meta: {
      title: "Video production — Modern, fast, fixed price | Fenny",
      description:
        "Video production for digital-first brands. Strategy, shoot, edit and channel-ready masters from a single senior team.",
    },
    page: {
      eyebrow: "Video production",
      heading: "Video production built for the way brands actually publish.",
      highlightIndices: [2],
      intro:
        "Video production tuned for digital-first marketing — every shoot day is mapped to the channels, formats and cadences your team actually needs.",
      whatItIs: {
        title: "Production designed around the publishing plan.",
        body: "We start from the channel map and content calendar, not from a wishlist of shots. That means every shoot day produces a balanced library of hero, mid-funnel and short-form assets.",
      },
      whenToUse: [
        "Always-on digital marketing",
        "Brands replacing project-by-project vendors",
        "Performance teams scaling paid creative",
        "Comms teams needing predictable output",
      ],
      whatsIncluded: [
        "Channel and asset map",
        "Production calendar with senior producer",
        "Shoot, edit, motion and sound",
        "Captions and platform-spec exports",
        "Optional Pulse subscription cadence",
      ],
      outcomes: [
        { metric: "Predictable", body: "fixed prices, fixed timelines, no surprise scopes." },
        { metric: "All channels", body: "one shoot delivers every format you need." },
        { metric: "Pulse-ready", body: "scale to monthly cadence when you're ready." },
      ],
    },
  },
  "film-agency": {
    enPath: "/en/film-agency",
    svPath: "/filmbyra",
    meta: {
      title: "Film agency — Strategy, story and senior craft | Fenny",
      description:
        "A film agency that owns the creative and the camera. Concept, script, shoot and post under one roof — no agency markup.",
    },
    page: {
      eyebrow: "Film agency",
      heading: "An agency that owns the creative and the camera.",
      highlightIndices: [1],
      intro:
        "A film agency that handles strategy, story and senior craft in one team — without the markup of buying creative from one agency and production from another.",
      whatItIs: {
        title: "Creative agency and production company in one.",
        body: "We sit somewhere between a traditional agency and a production company: senior strategists and directors working on the same project, with no hand-off between concept and execution.",
      },
      whenToUse: [
        "Brands frustrated with agency-to-production handoffs",
        "In-house teams needing strategic creative",
        "Multi-channel launches",
        "Long-term brand storytelling partnerships",
      ],
      whatsIncluded: [
        "Strategy and creative direction",
        "Concept and script development",
        "Full production and post",
        "Always-on creative partnership available",
      ],
      outcomes: [
        { metric: "One team", body: "no creative-vs-production hand-off." },
        { metric: "No markup", body: "the price you pay is the price of the work." },
        { metric: "Long-term", body: "we work as your outsourced creative team." },
      ],
    },
  },
  "video-agency": {
    enPath: "/en/video-agency",
    svPath: "/videobyra",
    meta: {
      title: "Video agency — Always-on video, all channels | Fenny",
      description:
        "Video agency for brands publishing on every channel. Strategy, production and editorial — built for cadence, not one-offs.",
    },
    page: {
      eyebrow: "Video agency",
      heading: "Your always-on video partner.",
      highlightIndices: [2],
      intro:
        "A video agency built for brands that publish weekly — strategy, shoot, edit and editorial cadence handled by one senior team.",
      whatItIs: {
        title: "Editorial cadence meets cinematic craft.",
        body: "We help marketing and comms teams move from one-off productions to a sustained editorial rhythm — without losing the craft that makes the work worth watching.",
      },
      whenToUse: [
        "Brands publishing weekly or monthly",
        "Performance and brand teams sharing creative",
        "Recruitment-led video programs",
        "Multi-market content rollouts",
      ],
      whatsIncluded: [
        "Editorial calendar with senior producer",
        "Monthly shoot days",
        "Edit, motion, captions, exports",
        "Channel-specific cutdowns",
        "Optional Pulse plan",
      ],
      outcomes: [
        { metric: "Cadence", body: "weekly or monthly publishing rhythm." },
        { metric: "Library", body: "compound a footage and asset library over time." },
        { metric: "One team", body: "no re-briefing every quarter." },
      ],
    },
  },
  "ad-agency": {
    enPath: "/en/ad-agency",
    svPath: "/reklamfilmsbyra",
    meta: {
      title: "Ad agency — Commercials that convert | Fenny",
      description:
        "An ad agency focused on commercials that earn attention and drive measurable response. Strategy, story and senior production.",
    },
    page: {
      eyebrow: "Ad agency",
      heading: "Ads that earn attention and move numbers.",
      highlightIndices: [1],
      intro:
        "An ad agency that treats every commercial as both a brand asset and a performance unit — strategy, story and senior production under one roof.",
      whatItIs: {
        title: "The agency for brands that want commercials to actually convert.",
        body: "Most ad agencies hand creative off to a separate production company; we keep both in-house, which means tighter feedback loops, faster turnaround and no markup stacking.",
      },
      whenToUse: [
        "Launching a new product or category",
        "Refreshing tired paid creative",
        "Replacing a traditional agency + production stack",
        "Going hard on paid media for a campaign quarter",
      ],
      whatsIncluded: [
        "Brand and campaign strategy",
        "Concept and script",
        "Senior director, DP and producer",
        "Edit, color, sound, motion",
        "Hero plus full cutdown set for every channel",
      ],
      outcomes: [
        { metric: "Brand + perf", body: "commercials that work as both brand and ad." },
        { metric: "Tight loop", body: "creative and production share the same room." },
        { metric: "Fixed price", body: "no surprise invoices on production." },
      ],
    },
  },
  "content-agency": {
    enPath: "/en/content-agency",
    svPath: "/contentbyra",
    meta: {
      title: "Content agency — Editorial-grade video at cadence | Fenny",
      description:
        "Content agency producing editorial-grade video and motion for brand, social and recruitment — at a sustainable monthly cadence.",
    },
    page: {
      eyebrow: "Content agency",
      heading: "Editorial-grade content. Sustainable cadence.",
      highlightIndices: [3],
      intro:
        "A content agency that produces brand, social and recruitment video at editorial cadence — without burning out internal teams or compromising the craft.",
      whatItIs: {
        title: "An editorial team that owns motion, copy and craft.",
        body: "We work the way a small in-house content team would, but with a senior production engine behind every brief. Plan, shoot, edit, publish — repeat.",
      },
      whenToUse: [
        "Building an always-on content program",
        "Replacing scattered freelancers",
        "Recruitment + employer brand at scale",
        "Multi-market content with shared visual system",
      ],
      whatsIncluded: [
        "Editorial planning",
        "Monthly or bi-weekly shoot days",
        "Edit, motion, captioning",
        "Channel-specific exports",
        "Optional Pulse plan",
      ],
      outcomes: [
        { metric: "Monthly", body: "sustainable cadence without burnout." },
        { metric: "Editorial", body: "consistent voice, visual and craft." },
        { metric: "Library", body: "growing asset library over time." },
      ],
    },
  },
  "film-producer": {
    enPath: "/en/film-producer",
    svPath: "/filmproducent",
    meta: {
      title: "Film producer — senior lead on every project | Fenny",
      description:
        "A senior film producer on every project — owning timeline, budget, crew and craft from brief to delivery.",
    },
    page: {
      eyebrow: "Film producer",
      heading: "A senior producer on every project. No juniors.",
      highlightIndices: [2],
      intro:
        "A senior film producer leads every Fenny project — owning timeline, budget, crew and craft from kickoff to final delivery, with no hand-off to juniors.",
      whatItIs: {
        title: "The most experienced person in the room runs your project.",
        body: "We don't sell a senior pitch and then hand you to a junior. The producer in the kickoff is the same producer on set and in the edit.",
      },
      whenToUse: [
        "High-stakes brand and campaign films",
        "Complex multi-location productions",
        "Tight timelines that need a steady hand",
        "Clients tired of agency bait-and-switch",
      ],
      whatsIncluded: [
        "Senior producer from kickoff to delivery",
        "Budget and timeline ownership",
        "Crew and location bookings",
        "Director and DP coordination",
        "Post oversight through master delivery",
      ],
      outcomes: [
        { metric: "Senior", body: "one producer, one owner, no juniors." },
        { metric: "On time", body: "97% of projects delivered on the agreed date." },
        { metric: "On budget", body: "fixed price commitments respected." },
      ],
    },
  },
  "employer-branding-video": {
    enPath: "/en/employer-branding-video",
    svPath: "/employer-branding-film",
    meta: {
      title: "Employer branding video — Tell the team story | Fenny",
      description:
        "Employer branding video that helps you hire the right people faster. Authentic culture stories, role films and recruitment content.",
    },
    page: {
      eyebrow: "Employer branding video",
      heading: "Show why people stay. Hire the ones who'd fit.",
      highlightIndices: [1],
      intro:
        "Employer branding video that tells the real story of working at your company — and helps recruiters fill the funnel with the right people, faster.",
      whatItIs: {
        title: "Culture, mission and roles — on film.",
        body: "We pair an employer-brand workshop with on-site filming to produce a hero culture film plus a library of role-specific shorts your recruiters can drop straight into LinkedIn campaigns.",
      },
      whenToUse: [
        "Scaling teams across multiple markets",
        "Opening new offices",
        "Filling hard-to-hire roles",
        "Refreshing a generic careers page",
      ],
      whatsIncluded: [
        "Employer-brand workshop",
        "On-site filming across 1-2 days",
        "Hero culture film",
        "Role-specific shorts",
        "Vertical cuts for LinkedIn, TikTok and Instagram",
      ],
      outcomes: [
        { metric: "-40%", body: "cost per qualified applicant on average." },
        { metric: "3×", body: "careers-page time-on-site with video." },
        { metric: "Library", body: "1 shoot produces many role variants." },
      ],
    },
  },
  // ============= Loop 5 — Pricing & comparison (A5) =============
  "video-subscription": {
    enPath: "/en/video-subscription",
    svPath: "/filmabonnemang",
    meta: {
      title: "Video subscription — Monthly video on a fixed budget | Fenny",
      description:
        "Video subscription plans for brands publishing weekly. Fixed monthly fee, senior team, predictable output.",
    },
    page: {
      eyebrow: "Video subscription",
      heading: "Monthly video. Fixed price. Senior team.",
      highlightIndices: [2],
      intro:
        "A video subscription gives you a senior team on a monthly retainer — predictable budget, predictable output, no per-project negotiations.",
      whatItIs: {
        title: "Pulse, Fenny's productised subscription model.",
        body: "Three tiers — Light, Core and Scale — covering anywhere from a few short-form drops a month to a full editorial program with hero films.",
      },
      whenToUse: [
        "Always-on social and recruitment programs",
        "Brands tired of per-project negotiation",
        "Comms teams needing a predictable cadence",
        "Scaling content without growing headcount",
      ],
      whatsIncluded: [
        "Monthly shoot or recording day",
        "Edit, motion, captions",
        "Channel-ready exports",
        "Quarterly editorial planning",
        "Senior producer as your single point of contact",
      ],
      outcomes: [
        { metric: "Fixed €", body: "predictable monthly invoice." },
        { metric: "Monthly", body: "ship something new every month, no slips." },
        { metric: "Senior", body: "same team every cycle, no re-briefing." },
      ],
    },
  },
  "subscription-video": {
    enPath: "/en/subscription-video",
    svPath: "/film-pa-prenumeration",
    meta: {
      title: "Subscription video — ship film every month | Fenny",
      description:
        "Subscription video service for brands that want continuous output without long contracts. Cancel anytime, ship every month.",
    },
    page: {
      eyebrow: "Subscription video",
      heading: "Continuous output. No long contracts.",
      highlightIndices: [1],
      intro:
        "Subscription video for brands that want continuous output without locking into a 12-month contract — three months minimum, cancel anytime after.",
      whatItIs: {
        title: "A subscription that flexes with your year.",
        body: "Switch tiers, pause for a month, or cancel after the minimum term. We're built for the way real marketing teams actually work across a year.",
      },
      whenToUse: [
        "Seasonal businesses with uneven calendars",
        "Brands testing always-on for the first time",
        "Teams replacing scattered freelancers",
        "Marketing leaders wanting flexible budget control",
      ],
      whatsIncluded: [
        "3-month minimum, cancel anytime after",
        "Tier switching between cycles",
        "Monthly shoot or recording day",
        "Full edit, motion and exports",
      ],
      outcomes: [
        { metric: "3 mo", body: "minimum commitment, then flexible." },
        { metric: "Tier switch", body: "scale up or down between cycles." },
        { metric: "Pause", body: "skip a month without losing your slot." },
      ],
    },
  },
  "pulse-vs-prime": {
    enPath: "/en/pulse-vs-prime",
    svPath: "/jamfor/pulse-vs-prime",
    meta: {
      title: "Pulse vs Prime — Which plan fits your team? | Fenny",
      description:
        "Pulse is monthly video at cadence. Prime is hero-grade campaigns and brand films. Here's how to choose.",
    },
    page: {
      eyebrow: "Compare",
      heading: "Pulse or Prime? Here's how to choose.",
      highlightIndices: [0],
      intro:
        "Pulse and Prime solve different problems. Pulse keeps your channels fed every month. Prime delivers the hero films that define a year.",
      whatItIs: {
        title: "Two products, one studio.",
        body: "Most brands eventually run both: Pulse for ongoing cadence, Prime for the big moments. You don't have to choose one forever — you choose what's right for this quarter.",
      },
      whenToUse: [
        "Pulse: always-on social, recruitment and product content",
        "Prime: brand films, commercials and launch campaigns",
        "Both: brands running an editorial program alongside hero moments",
      ],
      whatsIncluded: [
        "Pulse — monthly cadence, fixed price",
        "Prime — project-based, hero-grade craft",
        "Both share the same senior producer pool",
        "Switch or layer them as the year shifts",
      ],
      outcomes: [
        { metric: "Pulse", body: "fixed monthly fee, monthly output." },
        { metric: "Prime", body: "project price, hero-grade craft." },
        { metric: "Both", body: "many clients run them in parallel." },
      ],
    },
  },
  "agency-vs-freelancer": {
    enPath: "/en/agency-vs-freelancer",
    svPath: "/jamfor/filmbyra-vs-frilansare",
    meta: {
      title: "Film agency vs freelancer — Which is right for you? | Fenny",
      description:
        "Freelancers are flexible and cheap. Agencies bring senior craft and accountability. Here's how to decide which fits your project.",
    },
    page: {
      eyebrow: "Compare",
      heading: "Freelancer or film agency?",
      highlightIndices: [1],
      intro:
        "Freelancers are flexible and cheap, but you carry coordination risk. A film agency takes on the senior craft and the accountability — at a price.",
      whatItIs: {
        title: "Two models, two cost structures.",
        body: "Freelancers work best for narrow, well-defined briefs. Agencies pay off when you need senior strategy, multi-role coordination and someone accountable for the outcome.",
      },
      whenToUse: [
        "Freelancer: a single shoot day with a clear brief",
        "Agency: multi-asset campaigns and brand storytelling",
        "Agency: anything where downside risk matters",
        "Freelancer: ad-hoc cutdowns when an agency would be overkill",
      ],
      whatsIncluded: [
        "Freelancer — single role, hourly or day rate",
        "Agency — full team, fixed-price project",
        "Agency — strategic creative direction",
        "Agency — single point of accountability",
      ],
      outcomes: [
        { metric: "Freelancer", body: "lowest day rate, highest coordination cost." },
        { metric: "Agency", body: "fixed total price, lowest risk." },
        { metric: "Mixed", body: "many brands run both for different needs." },
      ],
    },
  },
  "subscription-vs-project": {
    enPath: "/en/subscription-vs-project",
    svPath: "/jamfor/filmabonnemang-vs-projektfilm",
    meta: {
      title: "Video subscription vs project pricing | Fenny",
      description:
        "Subscription video gives you cadence on a fixed monthly fee. Project film delivers hero moments. Here's how to decide.",
    },
    page: {
      eyebrow: "Compare",
      heading: "Subscription or project? It depends on the year.",
      highlightIndices: [2],
      intro:
        "Subscription video wins when you publish every month. Project film wins when you need one hero moment to define the year.",
      whatItIs: {
        title: "Cadence vs. peak — choose by your publishing reality.",
        body: "If you're shipping weekly, a subscription model is cheaper per asset. If you only need one or two flagship films a year, project pricing usually wins.",
      },
      whenToUse: [
        "Subscription: 4+ deliverables per month",
        "Project: 1-2 hero campaigns a year",
        "Both: brands with cadence and hero needs",
      ],
      whatsIncluded: [
        "Subscription — fixed monthly fee",
        "Project — fixed per-project price",
        "Both — senior producer-led",
      ],
      outcomes: [
        { metric: "Cadence", body: "subscription wins for steady output." },
        { metric: "Peaks", body: "project wins for one-off hero moments." },
        { metric: "Mixed", body: "many clients run both in parallel." },
      ],
    },
  },
  "inhouse-vs-agency": {
    enPath: "/en/inhouse-vs-agency",
    svPath: "/jamfor/inhouse-vs-filmbyra",
    meta: {
      title: "In-house vs film agency — When does each pay off? | Fenny",
      description:
        "Building an in-house video team gives control. Hiring an agency gives senior craft on demand. Here's the trade-off — with real numbers.",
    },
    page: {
      eyebrow: "Compare",
      heading: "In-house team or film agency?",
      highlightIndices: [3],
      intro:
        "An in-house team gives you total control and a deep brand library — at a fixed annual cost. An agency gives you senior craft on demand, scaled to your real need.",
      whatItIs: {
        title: "Capacity vs. craft — the real trade-off.",
        body: "In-house teams are unbeatable for volume and brand fluency. Agencies win on craft, peak capacity and not paying for downtime.",
      },
      whenToUse: [
        "In-house: 20+ deliverables per month, stable need",
        "Agency: variable need, multiple peaks per year",
        "Both: in-house volume + agency hero moments",
      ],
      whatsIncluded: [
        "In-house — salary, gear, software, downtime",
        "Agency — fixed per-project or monthly fee",
        "Both — senior producer leadership",
      ],
      outcomes: [
        { metric: "In-house", body: "wins above ~20 deliverables/month." },
        { metric: "Agency", body: "wins for variable or peak needs." },
        { metric: "Mixed", body: "common at scale: in-house + agency on tap." },
      ],
    },
  },
  "what-does-a-commercial-cost": {
    enPath: "/en/what-does-a-commercial-cost",
    svPath: "/vad-kostar-en-reklamfilm",
    meta: {
      title: "What does a commercial cost? — Real prices, no fluff | Fenny",
      description:
        "What a commercial actually costs in 2026 — broken down by tier, scope and channel. Real numbers from real productions.",
    },
    page: {
      eyebrow: "Pricing",
      heading: "What does a commercial actually cost?",
      highlightIndices: [1],
      intro:
        "Real 2026 prices for commercials in Sweden — broken down by scope, crew tier and channel mix, with no fluff.",
      whatItIs: {
        title: "Three tiers cover most real-world briefs.",
        body: "Most commercials in Sweden land in one of three price bands: lean digital ($), mid-tier multi-format ($$) or hero campaign with full crew ($$$).",
      },
      whenToUse: [
        "Budgeting a campaign for next quarter",
        "Briefing finance on realistic spend",
        "Comparing quotes from multiple agencies",
        "Pitching internally for budget approval",
      ],
      whatsIncluded: [
        "Lean tier — single-location shoot, hero + cutdowns",
        "Mid tier — multi-location, casting, full crew",
        "Hero tier — full creative, original score, broadcast-grade",
        "All tiers include channel-ready masters",
      ],
      outcomes: [
        { metric: "Lean", body: "150-300k SEK for digital-first commercials." },
        { metric: "Mid", body: "300-600k SEK for full-funnel campaigns." },
        { metric: "Hero", body: "600k SEK+ for broadcast-grade work." },
      ],
    },
  },
  "what-does-a-corporate-video-cost": {
    enPath: "/en/what-does-a-corporate-video-cost",
    svPath: "/vad-kostar-en-foretagsfilm",
    meta: {
      title: "What does a corporate video cost? — 2026 price guide | Fenny",
      description:
        "What a corporate video actually costs in 2026 — real prices for company films, interviews and recruitment-grade production.",
    },
    page: {
      eyebrow: "Pricing",
      heading: "What does a corporate video cost?",
      highlightIndices: [1],
      intro:
        "Real 2026 prices for corporate video — interviews, company films and recruitment-grade production, in three honest tiers.",
      whatItIs: {
        title: "Three tiers, three realistic budgets.",
        body: "Corporate video in Sweden typically lands at lean (interview-led), standard (full company film) or premium (multi-asset, multi-location).",
      },
      whenToUse: [
        "Refreshing your homepage hero",
        "Sales enablement film",
        "Recruitment and culture content",
        "Multi-asset internal comms",
      ],
      whatsIncluded: [
        "Lean — 1-day interview shoot, edit, captions",
        "Standard — 1-day company film with motion graphics",
        "Premium — multi-day, multi-location, full crew",
      ],
      outcomes: [
        { metric: "Lean", body: "60-120k SEK for interview-led films." },
        { metric: "Standard", body: "120-250k SEK for full company films." },
        { metric: "Premium", body: "250-500k SEK+ for multi-asset productions." },
      ],
    },
  },
  "how-long-should-a-commercial-be": {
    enPath: "/en/how-long-should-a-commercial-be",
    svPath: "/hur-lang-ska-en-reklamfilm-vara",
    meta: {
      title: "How long should a commercial be? — Format guide | Fenny",
      description:
        "The right commercial length depends on channel, intent and budget. Here's how to choose — by platform, with real benchmarks.",
    },
    page: {
      eyebrow: "Guide",
      heading: "How long should your commercial be?",
      highlightIndices: [0],
      intro:
        "There's no single right length for a commercial — it depends on channel, intent and how much story you're carrying. Here's a working guide.",
      whatItIs: {
        title: "Length is a strategic choice, not a default.",
        body: "Cut for the channel first, then for the intent. A 6-second YouTube bumper does a different job than a 60-second hero film on the homepage.",
      },
      whenToUse: [
        "6s — YouTube bumpers, unskippable awareness",
        "15s — paid social, short pre-roll",
        "30s — flagship paid social, TV",
        "60-90s — homepage hero, brand films",
      ],
      whatsIncluded: [
        "Hook in the first 1-2 seconds",
        "Clear single message per cut",
        "Captions for sound-off feeds",
        "Multiple lengths from the same shoot",
      ],
      outcomes: [
        { metric: "6-15s", body: "best for paid awareness." },
        { metric: "30s", body: "the workhorse format." },
        { metric: "60-90s", body: "for brand-led storytelling." },
      ],
    },
  },
  "video-price-calculator": {
    enPath: "/en/video-price-calculator",
    svPath: "/film-pris-kalkyl",
    meta: {
      title: "Video price calculator — estimate your budget | Fenny",
      description:
        "Estimate the price of your next video production in minutes. Tier, scope, crew and channel mix all in one calculator.",
    },
    page: {
      eyebrow: "Tool",
      heading: "Estimate your video budget in minutes.",
      highlightIndices: [2],
      intro:
        "A practical calculator that ballparks the price of your next production based on scope, crew tier and channel mix — before you book a single call.",
      whatItIs: {
        title: "A starting number you can take to finance.",
        body: "Our calculator is built on hundreds of real productions in Sweden. It won't replace a written quote, but it will give you a realistic number to plan around.",
      },
      whenToUse: [
        "Early-stage budget planning",
        "Comparing internal vs. external production",
        "Briefing finance before pitching the project",
        "Quick directional check before a call",
      ],
      whatsIncluded: [
        "Tier selector (lean / standard / hero)",
        "Channel mix selector",
        "Crew size and shoot day inputs",
        "Realistic price range output",
      ],
      outcomes: [
        { metric: "5 min", body: "to a realistic budget range." },
        { metric: "Real data", body: "based on hundreds of real productions." },
        { metric: "No call", body: "directional answer with zero pressure." },
      ],
    },
  },
  // ====== Loop 6 — Industries (D) ======
  "industries-construction": {
    enPath: "/en/industries/construction",
    svPath: "/branscher/bygg",
    meta: {
      title: "Video production for construction & infrastructure | Fenny",
      description:
        "Video for construction, infrastructure and real estate developers — site-safe crew, ID06, drone, time-lapse and recruitment films that don't disrupt production.",
    },
    page: {
      eyebrow: "Industry — Construction & infrastructure",
      heading: "Video for construction — by a crew that's actually allowed on site.",
      highlightIndices: [4, 5],
      intro:
        "We make video for construction, civil engineering and real estate developers. We know what ID06 means, we carry PPE and we speak the same language as the site safety lead. Project comms, recruitment, sustainability and sales — in a process that doesn't get in the way of the build.",
      whatItIs: {
        title: "Site-fluent production for long-running projects.",
        body: "Construction companies use video for safety training, employer branding, project documentation and customer cases. Fenny produces construction video via Pulse from €1,500/month: drone, time-lapse, interviews and field shoots with a safety-trained crew.",
      },
      whenToUse: [
        "Documenting a build from foundation to handover",
        "Recruiting trades and engineers in a talent-short market",
        "Sustainability reporting to investors and tenants",
        "Sales pitches for upcoming tenders and frameworks",
      ],
      whatsIncluded: [
        "Site-safety trained crew (ID06, hot work, PPE)",
        "Drone pilot with operator certificate",
        "Time-lapse rigs for multi-month builds",
        "Field interviews with workers and management",
        "Edit, color, sound and captioned masters",
        "Project library — many films from one shoot day",
      ],
      outcomes: [
        { metric: "12-24 mo", body: "useful life — construction films don't burn out fast." },
        { metric: "2-3×", body: "applications on recruitment ads featuring real trades." },
        { metric: "0 incidents", body: "we leave the site exactly as we found it." },
      ],
      faq: [
        { q: "Can you film on active sites?", a: "Yes. We have experience with site safety routines, BAS-U coordination, ID06 and PPE. All crew carry lift certification and hot-work cards where needed." },
        { q: "What do construction companies use the video for?", a: "Project communication to clients and end users, recruitment, employer branding, sustainability reporting, sales pitches for upcoming projects and reference cases." },
        { q: "How do you cover the whole build?", a: "A Pulse subscription gives you monthly filmed visits — from foundation to handover. The result is a time document that edits into many different stories." },
      ],
    },
  },
  "industries-ecommerce": {
    enPath: "/en/industries/ecommerce",
    svPath: "/branscher/e-handel",
    meta: {
      title: "Video production for ecommerce brands | Fenny",
      description:
        "Video for ecommerce brands — product video, paid social creatives, UGC, lifestyle. A continuous creative flow via the Pulse subscription.",
    },
    page: {
      eyebrow: "Industry — Ecommerce",
      heading: "Video production for ecommerce that actually scales paid social.",
      highlightIndices: [5, 6],
      intro:
        "We produce continuous creative for ecommerce brands — PDP product video, paid social ads, UGC-style content, lifestyle and campaign film. A Pulse subscription delivers 4-20 new creatives per month at a fixed budget — exactly what you need to beat creative fatigue.",
      whatItIs: {
        title: "Creative volume that wins the auction.",
        body: "Meta and TikTok reward volume. One winning creative plus 4-7 losing ones is still profitable when each production costs €300-800. That's how Pulse is structured: you pay for output, not for individual guesses.",
      },
      whenToUse: [
        "Continuously launching new SKUs or collections",
        "Scaling paid social spend without burning audiences",
        "Replacing tired creatives that have lost CTR",
        "Adding hero video to product detail pages",
      ],
      whatsIncluded: [
        "4-20 new paid social creatives per month (Pulse)",
        "PDP hero video — 8-15 products per studio day",
        "UGC creator network across Sweden",
        "Lifestyle shoots with talent and locations",
        "Format-native masters (9:16, 1:1, 16:9)",
        "Captioned and platform-ready delivery",
      ],
      outcomes: [
        { metric: "15-35%", body: "conversion lift on product pages with hero video." },
        { metric: "30-50%", body: "lower CPM with right-sized cutdowns." },
        { metric: "4-20", body: "fresh creatives per month at fixed cost." },
      ],
      faq: [
        { q: "How often do we need new creative for paid social?", a: "At least 4-8 new creatives per month to beat creative fatigue on Meta and TikTok. For larger spend (€100k+/month), often 12-20 per month." },
        { q: "UGC or polished product video?", a: "Both. UGC often outperforms top of funnel (lower CPM, higher CTR). Polished carries mid and bottom — brand value and PDP conversion. We mix in the same production." },
        { q: "Do you provide UGC creators?", a: "Yes. We have a network of creators across Sweden producing UGC-style content for paid social. We combine it with our production when that delivers best." },
      ],
    },
  },
  "industries-real-estate": {
    enPath: "/en/industries/real-estate",
    svPath: "/branscher/fastighet",
    meta: {
      title: "Video production for real estate & property | Fenny",
      description:
        "Video for property owners and managers — leasing, sales, sustainability reporting and employer branding. In-house drone crew and certified pilots.",
    },
    page: {
      eyebrow: "Industry — Real estate",
      heading: "Video for property — without the showing-clichés.",
      highlightIndices: [3, 4],
      intro:
        "Fenny produces video for property owners and managers. Leasing and sales material for commercial space. Sustainability reports that actually show the work. Employer branding for an industry in a talent crisis. In-house drone crew, certified pilots and the whole chain in one process.",
      whatItIs: {
        title: "Portfolio video, on schedule, at scale.",
        body: "Property companies use video for leasing, sales material, sustainability reporting, tenant communication, employer branding and investor presentations. We can shoot whole portfolios over 2-5 days with one delivery.",
      },
      whenToUse: [
        "Leasing or selling commercial space",
        "Annual sustainability and ESG reporting",
        "Tenant communication and portfolio updates",
        "Investor decks and roadshow materials",
      ],
      whatsIncluded: [
        "Drone with certified pilot (A1/A3)",
        "Property exterior and interior cinematography",
        "Tenant and management interviews",
        "Edit, color, music and motion graphics",
        "Multi-property packages with per-asset discount",
        "Sub-titled English and Swedish masters",
      ],
      outcomes: [
        { metric: "€1.5-3.5k", body: "per asset on portfolio leasing packages." },
        { metric: "2-5 days", body: "to cover an entire portfolio." },
        { metric: "3-5 yr", body: "useful life on brand and sustainability film." },
      ],
      faq: [
        { q: "What do property companies use video for?", a: "Leasing and sales material, sustainability reporting, tenant communication, employer branding and investor presentations." },
        { q: "What does property video cost?", a: "Simple leasing film with drone: €1.5-3.5k per asset. Brand film for portfolio companies: €8-25k. Pulse subscription for continuous material across the portfolio." },
        { q: "Can you film a whole portfolio?", a: "Yes. We package multi-asset productions with per-property discount. 2-5 shoot days, delivered in one organised folder." },
      ],
    },
  },
  "industries-health": {
    enPath: "/en/industries/health",
    svPath: "/branscher/halsa",
    meta: {
      title: "Video production for medtech & life science | Fenny",
      description:
        "Video for medtech, life science and healthcare providers. MDR-compliant marketing, patient consent and clinical-environment experience.",
    },
    page: {
      eyebrow: "Industry — Health & medtech",
      heading: "Video for medtech and health — with real compliance.",
      highlightIndices: [3, 4],
      intro:
        "Fenny produces video for medtech, life science and healthcare providers. We know MDR marketing requirements, patient consent and GDPR — without it killing the story. Product video, patient cases, clinical-study comms and employer branding for hard-to-recruit specialists.",
      whatItIs: {
        title: "Story-driven film, compliant by design.",
        body: "Compliance isn't a checkbox at the end — it's built into how we plan, shoot and edit. We've worked in clinics, operating rooms and labs, and we run consent and anonymisation by the book.",
      },
      whenToUse: [
        "Launching a medical device in MDR markets",
        "Patient cases with proper consent and review",
        "Clinical-study communication to investigators",
        "Employer branding for clinicians and researchers",
      ],
      whatsIncluded: [
        "MDR-aware concept and script review",
        "GDPR-compliant consent handling",
        "Clinical-environment hygiene protocols",
        "Patient interviews with anonymisation options",
        "Subtitled multilingual masters",
        "Long-form and short-form cutdowns",
      ],
      outcomes: [
        { metric: "0 takedowns", body: "from regulatory bodies on our delivered work." },
        { metric: "€5-12k", body: "for a single product film or interview." },
        { metric: "2-3×", body: "engagement on patient-case film vs. text testimonials." },
      ],
      faq: [
        { q: "Can you film patients and medtech products?", a: "Yes, with the right consents and compliance. We have experience with GDPR, patient consent, MDR marketing requirements and national medical agency guidelines." },
        { q: "What does video for health and medtech cost?", a: "Simple product film or interview: €5-12k. Campaign with multiple patient cases: €15-50k." },
        { q: "Do you film in operating rooms or clinics?", a: "Yes, with the right hygiene protocols and technical briefing. We've done several productions in clinical environments." },
      ],
    },
  },
  "industries-industry": {
    enPath: "/en/industries/industry",
    svPath: "/branscher/industri",
    meta: {
      title: "Video production for industry & manufacturing | Fenny",
      description:
        "Video for industrial companies — recruitment, customer cases, brand and process films. Experience in factories, workshops, ports and mining.",
    },
    page: {
      eyebrow: "Industry — Manufacturing",
      heading: "Video for industry and manufacturing — where people are the brand.",
      highlightIndices: [7, 8],
      intro:
        "Industrial companies sell similar products with relationships as the differentiator. Your people — fitters, engineers, salespeople — are your real brand. Filmed right, they also become your strongest recruitment channel. Fenny produces industry video with a crew that's used to factories, ports, workshops and heavy environments.",
      whatItIs: {
        title: "Recruitment, sales and process film for industrial B2B.",
        body: "Three things drive most for industrial companies: recruitment film showing a real workday in talent-short trades, customer cases for B2B sales processes, and process film for customers who need to understand manufacturing or supply chains.",
      },
      whenToUse: [
        "Recruiting fitters, operators and engineers",
        "B2B sales process needing customer-case proof",
        "Explaining a manufacturing process to buyers",
        "Refreshing brand assets that have a 3-5 year life",
      ],
      whatsIncluded: [
        "Heavy-environment-ready gear and crew",
        "Safety routines and PPE",
        "Worker and management interviews",
        "Process cinematography and slow-motion",
        "Brand, recruitment and customer-case formats",
        "Long-form and short-form cutdowns",
      ],
      outcomes: [
        { metric: "3-5 yr", body: "useful life on industrial brand film." },
        { metric: "2-3×", body: "applications on recruitment film with real workers." },
        { metric: "B2B-ready", body: "customer cases that close deals, not just look good." },
      ],
      faq: [
        { q: "Can you film in industrial environments?", a: "Yes. We have experience producing in heavy environments — factories, workshops, warehouses, ports, mines. We follow safety routines and bring gear that handles the environment." },
        { q: "Which films drive the most for industrial companies?", a: "Three things: (1) recruitment film showing a real workday — a powerful tool in talent-short trades, (2) customer cases and reference film for B2B sales, (3) process film for customers who need to understand manufacturing or supply chains." },
        { q: "How long does industrial video stay relevant?", a: "Longer than for consumer brands. A good brand or recruitment film for industry can run 3-5 years. That means a slightly higher initial investment pays back better than for fast-rotating B2C content." },
      ],
    },
  },
  "industries-public-sector": {
    enPath: "/en/industries/public-sector",
    svPath: "/branscher/offentlig-sektor",
    meta: {
      title: "Video production for public sector & government | Fenny",
      description:
        "Video for municipalities, regions, agencies and the public sector — information film, recruitment, training. GDPR-aware and multilingual delivery.",
    },
    page: {
      eyebrow: "Industry — Public sector",
      heading: "Video for the public sector — with routines that actually work.",
      highlightIndices: [3, 4],
      intro:
        "Fenny produces video for municipalities, regions, government agencies and the public sector. We know procurement, GDPR, confidentiality and multilingual versioning. Information film for citizens, recruitment for talent-short services, internal training, event film and political messaging.",
      whatItIs: {
        title: "Public-sector-ready production, end to end.",
        body: "We handle both direct-procured assignments below threshold and participate in framework procurements. We help draft procurement responses with you when needed.",
      },
      whenToUse: [
        "Citizen information that must be clear and accessible",
        "Recruiting in talent-short public services (care, schools)",
        "Internal training and onboarding",
        "Event film from conferences and political moments",
      ],
      whatsIncluded: [
        "GDPR-compliant production routines",
        "Confidentiality and consent handling",
        "Accessible captioning and sign-language options",
        "Multilingual versioning (English + minority languages)",
        "Framework-procurement-ready documentation",
        "Long-term archive and rights handling",
      ],
      outcomes: [
        { metric: "WCAG", body: "compliant captioning on every delivery." },
        { metric: "8+ languages", body: "in our translator and voice network." },
        { metric: "Framework", body: "experience across municipalities and regions." },
      ],
      faq: [
        { q: "Are you a procured framework supplier?", a: "We take both direct-procured assignments below threshold and participate in framework procurements. We have experience writing procurement responses with the client." },
        { q: "Can you film with confidentiality and GDPR-sensitive material?", a: "Yes. We have routines for care, schools, social services and justice — consent forms, anonymisation, secure storage and chain of custody. We go through GDPR questions at the first meeting." },
        { q: "Which formats do you do most in the public sector?", a: "Information film for citizens (short, clear, captioned), recruitment film (talent-short trades like care/preschool), internal training film, event film from conferences, and political/strategic messaging." },
        { q: "How do you handle multilingual delivery?", a: "Versioning in Swedish, English and the major minority languages (Arabic, Somali, Dari, Tigrinya) is included as an add-on option. We have translators and voices in our network." },
      ],
    },
  },
  "industries-restaurant": {
    enPath: "/en/industries/restaurant",
    svPath: "/branscher/restaurang",
    meta: {
      title: "Video production for hotels & restaurants | Fenny",
      description:
        "Video for restaurants, hotels and hospitality. Food-styling experience, social-first content and brand film for multi-unit operators.",
    },
    page: {
      eyebrow: "Industry — Restaurant & hospitality",
      heading: "Video for restaurants and hotels — that actually fills tables.",
      highlightIndices: [6, 7],
      intro:
        "Fenny produces video for restaurants, hotels and hospitality. Food-styling experience and the right gear for macro shots, slow-motion and steam effects. Social-first content that drives bookings, brand film for multi-unit operators.",
      whatItIs: {
        title: "Hospitality content built for the social feed.",
        body: "Same-day rough cuts for stories. Finished clips within a week. Hero film within two weeks. We're set up for hospitality's tempo and seasonality.",
      },
      whenToUse: [
        "Opening a new restaurant or hotel",
        "Refreshing the social feed every quarter",
        "Launching a seasonal menu or campaign",
        "Building brand assets for a multi-unit operator",
      ],
      whatsIncluded: [
        "Food stylist option for hero shoots",
        "Macro and slow-motion gear",
        "Talent and location scouting",
        "Same-day stories delivery",
        "5-10 social cuts per shoot day",
        "Hero film for site and paid media",
      ],
      outcomes: [
        { metric: "Same day", body: "stories rough cut delivered after shoot." },
        { metric: "1 week", body: "to finished social clips." },
        { metric: "€2.5-6k", body: "for a single shoot day with full social pack." },
      ],
      faq: [
        { q: "What does video for restaurants or hotels cost?", a: "1-day shoot + a pack of 5-10 social clips + 1 hero: €2.5-6k. Larger brand production: €8-20k. Pulse subscription for continuous social content if you have multiple units." },
        { q: "Do you film food and drink?", a: "Yes — we have specialised food-styling experience and the right gear for macro shots, slow-motion and steam effects." },
        { q: "How quickly can we get the material?", a: "Same-day rough cut for stories. Finished clips within 1 week. Hero film within 2 weeks." },
      ],
    },
  },
  "industries-saas": {
    enPath: "/en/industries/saas",
    svPath: "/branscher/saas",
    meta: {
      title: "Video production for SaaS & B2B tech | Fenny",
      description:
        "Video for SaaS companies — customer cases, product demos, animated explainers, founder-led content and event film. Strategy built for B2B tech.",
    },
    page: {
      eyebrow: "Industry — SaaS",
      heading: "Video for SaaS that actually drives pipeline.",
      highlightIndices: [3, 4],
      intro:
        "The product is invisible — you sell software. Most of the film has to be animation, UI walkthrough or customer talking head. Classic ad film with actors rarely works in B2B SaaS. Fenny produces SaaS video from seed to public — we know ARR, CAC, churn, PLG, ABM and pipeline vocabulary without you having to explain.",
      whatItIs: {
        title: "B2B-fluent production for software companies.",
        body: "We version every film for different segments and buyer personas. The same customer case can be cut in 3 versions targeting CFO, CTO and CMO with different hooks and emphasis.",
      },
      whenToUse: [
        "Launching a new feature or product line",
        "Building a customer-case library for sales enablement",
        "Founder-led LinkedIn content cadence",
        "Annual user conference or event film",
      ],
      whatsIncluded: [
        "Animated explainer or hybrid demo production",
        "Customer-case film with executive sponsors",
        "Founder talking head series for LinkedIn",
        "ABM segment-specific versioning",
        "Short-form feature highlights (9-22s)",
        "Event film with same-day cutdowns",
      ],
      outcomes: [
        { metric: "3 versions", body: "of one customer case for CFO, CTO and CMO." },
        { metric: "60-120s", body: "is the sweet spot for SaaS customer cases." },
        { metric: "ABM-ready", body: "personas, hooks and CTAs baked into the edit." },
      ],
      faq: [
        { q: "Why is video production for SaaS different?", a: "The product is invisible — you sell software. Most of the film has to be animation, UI walkthrough or customer talking head. Classic ad film with actors rarely works in B2B SaaS." },
        { q: "Which formats work best for SaaS?", a: "Customer-case film (60-120s), explainer/product demo (animated or hybrid), founder-led talking head for LinkedIn, short feature highlights (9-22s on social), and event film from user conferences." },
        { q: "How do you work with ABM and RevOps?", a: "We version every film for different segments and buyer personas. The same customer case can be cut in 3 versions targeting CFO, CTO and CMO with different hooks and emphasis." },
        { q: "Are you used to SaaS vocabulary?", a: "Yes. We've produced for SaaS clients from seed to public — we understand ARR, CAC, churn, PLG, ABM and pipeline vocabulary without you having to explain." },
      ],
    },
  },
  // ====== Audience-angle pages (mirror /branscher/video-for-*) ======
  "audience-b2b": {
    enPath: "/en/video-for-b2b",
    svPath: "/branscher/video-for-b2b",
    meta: {
      title: "B2B video — film that drives pipeline | Fenny",
      description:
        "B2B video for companies that sell to companies. Customer cases, product demos, thought leadership and sales enablement — produced continuously for long sales cycles and multiple decision-makers.",
    },
    page: {
      eyebrow: "Audience — B2B",
      heading: "B2B video that drives pipeline, not vanity views.",
      highlightIndices: [3, 4],
      intro:
        "Film for companies that sell to companies. We produce customer cases, product demos, thought leadership and sales enablement film that speaks to the whole buying group — CFO, CTO and CMO — across a long sales cycle.",
      whatItIs: {
        title: "Film built for the B2B funnel.",
        body: "B2B buying takes months and involves several decision-makers, so the film has to work for multiple roles rather than one broad audience. We produce it continuously via the Pulse subscription so you stay relevant through the whole journey.",
      },
      whenToUse: [
        "Building a customer-case library for sales enablement",
        "Running a founder- or expert-led thought-leadership cadence",
        "Shortening the sales cycle with product demos",
        "Keeping multiple buyer personas warm across a long deal",
      ],
      whatsIncluded: [
        "Customer-case and customer-story film",
        "Product demo and animated explainer",
        "Founder- and expert-led LinkedIn content",
        "Sales enablement film for account executives",
        "Persona-specific versioning (CFO / CTO / CMO)",
        "Event and conference film with cutdowns",
      ],
      outcomes: [
        { metric: "Full funnel", body: "awareness, consideration and decision film from one story." },
        { metric: "Multi-persona", body: "one case cut for CFO, CTO and CMO." },
        { metric: "2-8 clips/mo", body: "continuous output via the Pulse subscription." },
      ],
      faq: [
        { q: "What is B2B video?", a: "B2B video is film aimed at other companies rather than consumers — customer cases, product demos, thought leadership and sales enablement. The cycles are longer and the buying group larger, so the film must speak to several roles." },
        { q: "Which formats drive the most pipeline?", a: "Customer-case film (social proof), product demo/explainer (explains the abstract), founder- and expert-led LinkedIn content (builds trust over time) and sales enablement film that account executives send inside the deal." },
        { q: "Is one film enough, or do we need continuous production?", a: "B2B buying takes months and involves several decision-makers. A continuous flow of film keeps you top-of-mind through the whole funnel, which is why we produce B2B video continuously via the Pulse subscription." },
        { q: "How do you measure impact on B2B film?", a: "We tie film to funnel stages: view-through and engagement at the top, demo bookings and pipeline influence in the middle, and win-rate plus deal velocity at the bottom — not just vanity views." },
      ],
    },
  },
  "audience-growth": {
    enPath: "/en/video-for-growth-companies",
    svPath: "/branscher/video-for-tillvaxtbolag",
    meta: {
      title: "Video for growth companies — scale with film | Fenny",
      description:
        "Video for growth companies and scale-ups. Customer cases, product demos, founder-led content and employer branding at a pace that matches your growth — produced continuously via subscription.",
    },
    page: {
      eyebrow: "Audience — Growth companies",
      heading: "Film that scales with the business.",
      highlightIndices: [1],
      intro:
        "For growth companies and scale-ups that need a lot of film, often and fast. We produce customer cases, product demos, founder-led content and employer branding at a pace that matches your growth — without you hiring an in-house film team.",
      whatItIs: {
        title: "A predictable film flow built for scale-up speed.",
        body: "Growth companies win on frequency and relevance, not on one expensive production a year. A subscription gives a predictable flow at a fixed cost and scales between package tiers as your needs grow.",
      },
      whenToUse: [
        "Proving the product delivers with customer cases",
        "Building brand fast with founder-led content",
        "Recruiting at speed with employer branding film",
        "Feeding paid, social and sales with fresh film monthly",
      ],
      whatsIncluded: [
        "Customer-case and product-demo film",
        "Founder-led LinkedIn content series",
        "Employer branding film for fast hiring",
        "Short-form social cutdowns",
        "Scalable monthly output between package tiers",
        "Dedicated senior team at a fixed monthly price",
      ],
      outcomes: [
        { metric: "Scalable", body: "adjust monthly clips between tiers as you grow." },
        { metric: "No hires", body: "a dedicated team without building one in-house." },
        { metric: "Fast start", body: "first clips typically within a couple of weeks." },
      ],
      faq: [
        { q: "What film does a growth company need?", a: "Customer cases that prove the product delivers, product demos that shorten the sales cycle, founder-led content to build brand fast, and employer branding film to recruit at pace — all at a tempo that matches the growth." },
        { q: "Why does a subscription beat one-off projects?", a: "Growth companies need a lot of film, often and fast — not one big production a year. A subscription gives a predictable flow at a fixed cost without hiring an in-house film team." },
        { q: "Can you keep up as we scale?", a: "Yes. Pulse scales with you — you can adjust the number of clips per month between package tiers as the need grows, without a new procurement every time." },
        { q: "How fast do we get the first film?", a: "After a strategy session and a shared production day we usually deliver the first clips within a couple of weeks, then in a continuous monthly flow." },
      ],
    },
  },
  "audience-marketing": {
    enPath: "/en/video-for-marketing-teams",
    svPath: "/branscher/video-for-marknadsavdelningar",
    meta: {
      title: "Video for marketing teams — a continuous film flow | Fenny",
      description:
        "Video for marketing teams. Campaign film, short-form, customer cases and thought leadership in a continuous flow against your content calendar — without hiring an in-house video producer.",
    },
    page: {
      eyebrow: "Audience — Marketing teams",
      heading: "A continuous film flow against your content calendar.",
      highlightIndices: [2],
      intro:
        "For CMOs and marketing teams that need film across several channels every month. We produce campaign film, short-form, customer cases and thought leadership in a predictable flow — planned against your calendar, without hiring an in-house video producer.",
      whatItIs: {
        title: "The film engine behind your content calendar.",
        body: "Marketing teams need 4-10 new films a month for paid, LinkedIn, web and email. A subscription gives a continuous flow against the content calendar at a fixed cost, planned around your campaign moments.",
      },
      whenToUse: [
        "Filling the content calendar across channels",
        "Testing more hooks on paid social",
        "Keeping the brand alive between big campaigns",
        "Replacing freelancer chasing with a fixed team",
      ],
      whatsIncluded: [
        "Campaign film and ad film",
        "Short-form for LinkedIn and social",
        "Customer cases and thought leadership",
        "Web and landing-page film",
        "Channel- and format-specific versioning",
        "Production planned against your calendar",
      ],
      outcomes: [
        { metric: "4-10/mo", body: "new clips across paid, LinkedIn, web and email." },
        { metric: "On-calendar", body: "production planned around your campaign moments." },
        { metric: "No in-house", body: "a dedicated team at a fixed monthly price." },
      ],
      faq: [
        { q: "How does film help a marketing team?", a: "Film is the format that performs best on most channels — paid social, LinkedIn, web and email. A marketing team with a continuous film flow can fill the calendar, test more hooks and keep the brand alive without chasing freelancers for every campaign." },
        { q: "How many films do we need per month?", a: "It depends on channel mix and tempo, but most marketing teams we work with use 4-10 new clips a month: campaign film, short-form for social, customer cases and thought leadership. The Pulse packages cover 2-8 production clips a month." },
        { q: "Can you work against our content calendar?", a: "Yes. We plan production against your campaign and content calendar so films land when you need them — not weeks after the campaign started." },
        { q: "Do we avoid hiring an in-house video producer?", a: "Yes, that's the whole point of the subscription. You get a dedicated team, a predictable monthly price and a continuous flow — without recruitment, equipment or fixed staffing costs." },
      ],
    },
  },
  "audience-sales": {
    enPath: "/en/video-for-sales-teams",
    svPath: "/branscher/video-for-saljteam",
    meta: {
      title: "Video for sales teams — sales enablement film | Fenny",
      description:
        "Video for sales teams. Customer cases, product demos, ROI film and personal sales enablement video that shorten the sales cycle and move deals forward — produced continuously via subscription.",
    },
    page: {
      eyebrow: "Audience — Sales teams",
      heading: "Film that shortens the sales cycle.",
      highlightIndices: [1],
      intro:
        "For sales teams and account executives who want to move deals forward with film. We produce customer cases, product demos, ROI film and personal sales enablement video — versioned per decision-maker and refreshed continuously in step with the pipeline.",
      whatItIs: {
        title: "Film built for the sales process.",
        body: "Sales enablement video is film built for the deal rather than broad marketing — product walkthroughs, objection handling, ROI explainers and short personal videos that account executives send straight to the buyer.",
      },
      whenToUse: [
        "Giving reps social proof to send inside the deal",
        "Answering buyer questions before the meeting",
        "Handling objections and explaining ROI",
        "Keeping deals warm between touchpoints",
      ],
      whatsIncluded: [
        "Customer-case film for social proof",
        "Product demo and explainer",
        "ROI and objection-handling film",
        "Personal sales enablement video",
        "Persona-specific versioning (CFO / CTO / CMO)",
        "Fresh material every month via subscription",
      ],
      outcomes: [
        { metric: "Shorter cycle", body: "demos answer questions before the meeting." },
        { metric: "Per persona", body: "one case cut for each decision-maker." },
        { metric: "Always fresh", body: "new cases and updated demos every month." },
      ],
      faq: [
        { q: "How does film help a sales team?", a: "Film shortens the sales cycle. Customer cases give social proof, product demos answer questions before the meeting, and personal sales enablement video keeps the deal alive between touchpoints. Reps get material that actually moves deals forward." },
        { q: "What is sales enablement video?", a: "It's film built for the sales process rather than broad marketing — product walkthroughs, objection handling, ROI explainers and short personal greeting videos that account executives send straight to the buyer." },
        { q: "Do we need different versions per decision-maker?", a: "Often, yes. In B2B deals several roles sit in the buying group. We cut the same customer case or demo in versions aimed at CFO, CTO and CMO with different hooks and emphasis." },
        { q: "Can we get film continuously in step with the pipeline?", a: "Yes. Via Pulse we produce new sales enablement material every month — new customer cases, updated demos and campaign support — so the sales team always has fresh film." },
      ],
    },
  },
  // ====== Loop 6 — Misc (A6) ======
  "misc-faq": {

    enPath: "/en/faq",
    svPath: "/faq",
    meta: {
      title: "FAQ — Frequently asked questions | Fenny",
      description:
        "Answers on Pulse, Prime, Pilot, our process, pricing and delivery. If you can't find it here, just ask — it costs nothing.",
    },
    page: {
      eyebrow: "FAQ",
      heading: "Answers to what you usually ask.",
      highlightIndices: [3, 4],
      intro:
        "The questions we get most, answered honestly. Pulse vs. Prime vs. Pilot, what's included, what isn't, how fast we move and what a typical price range looks like.",
      whatItIs: {
        title: "Subscriptions, projects and ad-hoc — explained.",
        body: "Pulse is a monthly video subscription for continuous output. Prime is a senior, hand-crafted project for the productions that need to land hard. Pilot is a single test production at a fixed price.",
      },
      whenToUse: [
        "Choosing between Pulse, Prime and Pilot",
        "Understanding pricing without booking a call",
        "Mapping our process to your internal timeline",
        "Checking delivery formats, rights and revisions",
      ],
      whatsIncluded: [
        "Subscription scope — what fits inside Pulse",
        "Project scope — what makes a Prime production",
        "Pricing ranges with no hidden line items",
        "Timelines, revisions and rights",
        "Crew, gear and locations we cover",
        "Delivery formats and platform-ready masters",
      ],
      outcomes: [
        { metric: "Same day", body: "answers on weekdays — usually within hours." },
        { metric: "No call", body: "needed to get a directional answer." },
        { metric: "Fixed price", body: "on every package we quote." },
      ],
      faq: [
        { q: "What does a typical Pulse subscription cost?", a: "Pulse starts at €1,500/month and scales with the volume and tier you choose. Most clients land between €1,500 and €5,000/month." },
        { q: "How fast can you start?", a: "Most Pulse subscriptions go live within 2 weeks. Prime projects start with concept work within 1 week and shoot 3-6 weeks later." },
        { q: "Can we keep the rights?", a: "Yes. All productions transfer full usage rights to you on delivery, including paid media." },
        { q: "Do you work outside Sweden?", a: "Yes. We've shot across Europe and the Nordics. Travel and per diem are quoted transparently." },
      ],
      cta: { primary: { label: "Book a 30-min call", to: "/en/contact" }, secondary: { label: "See pricing", to: "/en/pricing" } },
    },
  },
  "misc-glossary": {
    enPath: "/en/glossary",
    svPath: "/ordlista",
    meta: {
      title: "Video production glossary — terms explained | Fenny",
      description:
        "A plain-English glossary of video production terms — formats, roles, gear, delivery and pricing concepts.",
    },
    page: {
      eyebrow: "Glossary",
      heading: "Video production, in plain English.",
      highlightIndices: [3, 4],
      intro:
        "We translate the jargon. Formats, roles, gear, delivery and pricing concepts explained the way we'd explain them to a friend who's never bought video before.",
      whatItIs: {
        title: "Vocabulary that makes briefs easier.",
        body: "Knowing the difference between a hero film and a cutdown, or between a DP and a producer, makes every brief sharper and every quote easier to read.",
      },
      whenToUse: [
        "Writing your first video brief",
        "Comparing quotes from different agencies",
        "Onboarding a new marketing hire",
        "Aligning internal stakeholders on scope",
      ],
      whatsIncluded: [
        "Formats: hero, cutdown, BTS, UGC, explainer",
        "Roles: producer, DP, director, editor, colorist",
        "Gear: cinema camera, gimbal, drone, prime lens",
        "Delivery: master, ProRes, H.264, captions, subs",
        "Pricing: day rate, fixed price, retainer",
        "Rights: usage, exclusivity, buyout",
      ],
      outcomes: [
        { metric: "100+", body: "terms explained in plain English." },
        { metric: "No jargon", body: "for the sake of jargon." },
        { metric: "Shareable", body: "with stakeholders who don't live in video." },
      ],
      cta: { primary: { label: "Book a 30-min call", to: "/en/contact" }, secondary: { label: "See pricing", to: "/en/pricing" } },
    },
  },
  "misc-partners": {
    enPath: "/en/partners",
    svPath: "/partners",
    meta: {
      title: "Partner program — three tiers | Fenny",
      description:
        "Referral, white-label reseller and strategic partner. Transparent margins, NDA within 24 hours, and a team agencies actually want to work with.",
    },
    page: {
      eyebrow: "Partner program",
      heading: "We'd rather grow with the right partners than with more of them.",
      highlightIndices: [4, 5],
      intro:
        "Three clear tiers — referral, white-label reseller and strategic partner. Pick the shape that matches how you sell, and we handle production in the background.",
      whatItIs: {
        title: "Three tiers, one playbook.",
        body: "Referral pays 10% on the first invoice. Reseller is full white-label with 15–20% margin and NDA within 24 hours. Strategic is recurring co-production with priority booking and co-marketing — built for platforms and sister agencies like Norrhavet, Hasty, Ellary, Extreprenour and Swedish Frame.",
      },
      whenToUse: [
        "You tip clients but don't want delivery risk (Referral)",
        "You sell video in your own name and need a crew (Reseller)",
        "You're a platform or sister agency with recurring needs (Strategic)",
        "You're a freelancer needing a full production team",
      ],
      whatsIncluded: [
        "Referral: 10% of first invoice, zero delivery risk",
        "Reseller: 15–20% margin, white-label delivery",
        "Strategic: priority booking, dedicated PM, co-marketing",
        "NDA signed within 24 hours on all tiers",
        "Clean scope, clean margin, clean handoff",
        "Digital partner agreement — no lock-in",
      ],
      outcomes: [
        { metric: "24h", body: "NDA in place on every tier." },
        { metric: "10–20%", body: "transparent margin or referral fee." },
        { metric: "4 steps", body: "from intro to signed partner." },
      ],
      cta: { primary: { label: "Apply to the program", to: "/en/contact" }, secondary: { label: "See our work", to: "/en/work" } },
    },
  },
  "misc-press": {
    enPath: "/en/press",
    svPath: "/press",
    meta: {
      title: "Press — Fenny in the news and industry data",
      description:
        "Press releases, reports and editorial mentions. Logos, founder bios and high-resolution images available on request.",
    },
    page: {
      eyebrow: "Press",
      heading: "What we publish, and where we get covered.",
      highlightIndices: [4, 5],
      intro:
        "Fenny publishes industry reports, benchmarks and editorial content on the Swedish and Nordic video production market. Journalists and editors — see below for materials, bios and contact.",
      whatItIs: {
        title: "Sources, data and stories — usable as-is.",
        body: "We publish first-hand data on production costs, agency vs. in-house economics and the Nordic video market. Everything is citable, attributed and available in high-resolution.",
      },
      whenToUse: [
        "Writing about the Swedish video production market",
        "Sourcing benchmarks for cost or output",
        "Looking for founder commentary or quotes",
        "Needing high-resolution images and logos",
      ],
      whatsIncluded: [
        "Annual market report on commercial video",
        "Press releases on major launches",
        "Founder bios and headshots (300 dpi)",
        "Brand logos in SVG and PNG",
        "Editorial-rights stills from selected productions",
        "Direct line to founder for comments",
      ],
      outcomes: [
        { metric: "Same day", body: "responses on press inquiries." },
        { metric: "Full bios", body: "and 300dpi headshots ready to use." },
        { metric: "Citable", body: "data with clear methodology." },
      ],
      cta: { primary: { label: "Email the press desk", to: "/en/contact" }, secondary: { label: "Read the 2026 report", to: "/en/reports/commercial-2026" } },
    },
  },
  "misc-for-journalists": {
    enPath: "/en/for-journalists",
    svPath: "/for-journalister",
    meta: {
      title: "For journalists — sources, data, comments | Fenny",
      description:
        "Resources for journalists writing about the Nordic video production market — benchmarks, methodology, founder comment and high-resolution images.",
    },
    page: {
      eyebrow: "For journalists",
      heading: "Sources, data and same-day comments.",
      highlightIndices: [3, 4],
      intro:
        "If you're writing about the Nordic video production market — costs, talent, agency vs. in-house, AI, formats — we're a source. We publish first-hand benchmarks, share methodology and turn around comment same-day.",
      whatItIs: {
        title: "Working with deadlines, not against them.",
        body: "Journalists need fast, accurate, attributable answers. We default to clear methodology, named sources and high-resolution image rights — so you can ship the piece without a back-and-forth.",
      },
      whenToUse: [
        "Writing about ad-spend, video budgets or production costs",
        "Comparing in-house vs. agency economics",
        "Covering AI in video production",
        "Needing Nordic market context for an international piece",
      ],
      whatsIncluded: [
        "Same-day comment from founder (CET hours)",
        "Citable benchmark data with methodology",
        "Founder bios and 300dpi headshots",
        "High-resolution editorial stills",
        "Anonymous case-data on request (under NDA)",
        "Translator-ready quotes in Swedish and English",
      ],
      outcomes: [
        { metric: "Same day", body: "on inquiries received before 14:00 CET." },
        { metric: "On record", body: "by default — no off-the-record runaround." },
        { metric: "Methodology", body: "transparent on every benchmark we publish." },
      ],
      cta: { primary: { label: "Email the press desk", to: "/en/contact" }, secondary: { label: "Read the 2026 report", to: "/en/reports/commercial-2026" } },
    },
  },
  "misc-privacy": {
    enPath: "/en/privacy",
    svPath: "/integritet",
    meta: {
      title: "Privacy & cookies — how Fenny handles your data",
      description:
        "How Fenny handles cookies, analytics and personal data. First-party, anonymous, no third-party advertising trackers.",
    },
    page: {
      eyebrow: "Privacy & cookies",
      heading: "First-party, anonymous, no surveillance.",
      highlightIndices: [3, 4],
      intro:
        "We use our own first-party, anonymous analytics to understand how the site is used. No cookies or tracking IDs are set before you actively accept them via our cookie banner.",
      whatItIs: {
        title: "Less data, less risk, same insight.",
        body: "Most marketing sites overcollect by default. We collect the minimum we need to improve the site and respond to inquiries — and nothing we'd be uncomfortable explaining to you in person.",
      },
      whenToUse: [
        "Understanding what we collect (and don't)",
        "Knowing how to opt out of analytics",
        "Requesting data export or deletion",
        "Reviewing our data handling for procurement",
      ],
      whatsIncluded: [
        "Anonymous visitor and session IDs (local storage)",
        "Page views, referrer, UTM parameters",
        "Device, browser, OS, language, country (via IP — not stored)",
        "No IP stored in clear text",
        "No third-party advertising data sharing",
        "No cross-site tracking",
      ],
      outcomes: [
        { metric: "0", body: "third-party advertising trackers." },
        { metric: "Local", body: "storage only for analytics IDs." },
        { metric: "On request", body: "data export, correction, deletion." },
      ],
      cta: { primary: { label: "Contact us", to: "/en/contact" }, secondary: { label: "See pricing", to: "/en/pricing" } },
    },
  },
  "misc-report-commercial-2026": {
    enPath: "/en/reports/commercial-2026",
    svPath: "/rapport/reklamfilm-2026",
    meta: {
      title: "The Nordic commercial video report 2026 | Fenny",
      description:
        "Fenny's annual report on the Nordic commercial video market — budgets, formats, channels, in-house vs. agency, AI adoption.",
    },
    page: {
      eyebrow: "Report 2026",
      heading: "The Nordic commercial video report 2026.",
      highlightIndices: [4, 5],
      intro:
        "Our annual report on the Nordic commercial video market — budgets, format mix, channel performance, in-house vs. agency economics and AI adoption across the industry. Based on first-hand data from hundreds of productions and a survey of Nordic marketing leaders.",
      whatItIs: {
        title: "First-hand data — not vendor marketing.",
        body: "Methodology is open. Sample sizes are stated. Every claim is sourced. Use the data freely with attribution. We update annually with the next year's production data.",
      },
      whenToUse: [
        "Benchmarking your video budget against the market",
        "Building a business case for in-house vs. agency",
        "Picking the right format mix for your category",
        "Briefing leadership on AI's real impact on video",
      ],
      whatsIncluded: [
        "Median production costs by format and channel",
        "Spend benchmarks by company size and industry",
        "In-house vs. agency total-cost-of-ownership",
        "AI adoption and where it actually saves time",
        "Channel performance: paid social, YouTube, TV, OOH",
        "PDF download and citable data tables",
      ],
      outcomes: [
        { metric: "200+", body: "productions analysed in this year's data." },
        { metric: "Citable", body: "with clear methodology on every chart." },
        { metric: "Free", body: "to download and quote with attribution." },
      ],
      cta: { primary: { label: "Download the report", to: "/en/contact" }, secondary: { label: "Press release", to: "/en/press/commercial-2026-report" } },
    },
  },
  "misc-press-commercial-2026-report": {
    enPath: "/en/press/commercial-2026-report",
    svPath: "/press/reklamfilm-2026-rapport",
    meta: {
      title: "Press release — Nordic commercial video report 2026 | Fenny",
      description:
        "Press release: Fenny publishes the 2026 Nordic commercial video report. Headline findings, methodology and media contact.",
    },
    page: {
      eyebrow: "Press release",
      heading: "Fenny releases the 2026 Nordic commercial video report.",
      highlightIndices: [5, 6],
      intro:
        "Stockholm — Fenny today publishes its annual report on the Nordic commercial video market. The report covers budgets, formats, channels, in-house vs. agency economics and AI adoption — based on first-hand data from over 200 productions.",
      whatItIs: {
        title: "Headline findings.",
        body: "Median commercial production budgets stayed flat year-over-year in nominal terms but fell 4-7% in real terms. AI is adopted in 60% of productions for at least one task — most often script ideation or rough cut — but only 8% report material time savings. In-house teams now produce 38% of total volume by count, up from 31% in 2024.",
      },
      whenToUse: [
        "Quoting headline findings in editorial",
        "Sourcing a comment from the founder",
        "Linking to the full report PDF",
        "Requesting the dataset under NDA",
      ],
      whatsIncluded: [
        "Headline findings with sample sizes",
        "Methodology and survey design",
        "Founder bio and 300dpi headshot",
        "Brand logos in SVG and PNG",
        "Direct line to press desk",
        "Embargo and exclusive options on request",
      ],
      outcomes: [
        { metric: "200+", body: "productions in the underlying dataset." },
        { metric: "Same day", body: "on press inquiries before 14:00 CET." },
        { metric: "Free", body: "to download and quote with attribution." },
      ],
      cta: { primary: { label: "Read the full report", to: "/en/reports/commercial-2026" }, secondary: { label: "Email the press desk", to: "/en/contact" } },
    },
  },
};
